import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";
import Chip from "@material-ui/core/Chip";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControl from "@material-ui/core/FormControl";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import InputLabel from "@material-ui/core/InputLabel";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import Select from "@material-ui/core/Select";
import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import AddIcon from "@material-ui/icons/Add";
import AlternateEmailIcon from "@material-ui/icons/AlternateEmail";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CheckBoxOutlinedIcon from "@material-ui/icons/CheckBoxOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import EditIcon from "@material-ui/icons/Edit";
import EventOutlinedIcon from "@material-ui/icons/EventOutlined";
import FormatListBulletedIcon from "@material-ui/icons/FormatListBulleted";
import LinkIcon from "@material-ui/icons/Link";
import NotesOutlinedIcon from "@material-ui/icons/NotesOutlined";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import PhoneIcon from "@material-ui/icons/Phone";
import TitleIcon from "@material-ui/icons/Title";
import ViewColumnIcon from "@material-ui/icons/ViewColumn";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import ConfirmationModal from "../../components/ConfirmationModal";
import TicketAgendaModal from "../../components/TicketAgendaModal";
import TicketOrdersModal from "../../components/TicketOrdersModal";
import TicketProcessesModal from "../../components/TicketProcessesModal";
import TicketRemindersModal from "../../components/TicketRemindersModal";
import TicketStreamingModal from "../../components/TicketStreamingModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const NO_GROUP_COLUMN = "__no_group_column__";
const ROW_DRAG_TYPE = "application/x-workspace-row";
const COLUMN_DRAG_TYPE = "application/x-workspace-column";
const PROPERTY_COLUMN_DRAG_TYPE = "application/x-workspace-property-column";
const DEFAULT_OPTION_COLOR = "#C7D2FE";
const CONTACT_DATA_CUSTOM_FIELD = "__custom_field__";
const LOADING_FUNNELS = "__loading_funnels__";
const NO_FUNNEL_STAGE = "__no_funnel_stage__";
const NO_SELECT_VALUE = "__no_select_value__";
const NO_CALENDAR_DATE_COLUMN = "__no_calendar_date_column__";
const WORKSPACE_FILTER_TRUE = "__workspace_filter_true__";
const WORKSPACE_FILTER_FALSE = "__workspace_filter_false__";
const WORKSPACE_FILTER_ALL = "__workspace_filter_all__";
const NO_WORKSPACE_BASE = "__no_workspace_base__";

const optionPalette = [
  "#C7D2FE",
  "#BAE6FD",
  "#BBF7D0",
  "#FEF3C7",
  "#FED7AA",
  "#FBCFE8",
  "#DDD6FE",
  "#E5E7EB",
];

const propertyTypes = [
  { value: "text", label: "Texto", icon: <NotesOutlinedIcon fontSize="small" /> },
  { value: "longText", label: "Área de texto", icon: <NotesOutlinedIcon fontSize="small" /> },
  { value: "number", label: "Número", icon: <TitleIcon fontSize="small" /> },
  { value: "currency", label: "Monto", icon: <TitleIcon fontSize="small" /> },
  { value: "select", label: "Seleccionar", icon: <FormatListBulletedIcon fontSize="small" /> },
  { value: "multiSelect", label: "Selección múltiple", icon: <FormatListBulletedIcon fontSize="small" /> },
  { value: "status", label: "Estado", icon: <ViewColumnIcon fontSize="small" /> },
  { value: "date", label: "Fecha", icon: <EventOutlinedIcon fontSize="small" /> },
  { value: "person", label: "Persona", icon: <PersonOutlineIcon fontSize="small" /> },
  { value: "contact", label: "Contacto", icon: <PersonOutlineIcon fontSize="small" /> },
  { value: "contactData", label: "Datos de contacto", icon: <PersonOutlineIcon fontSize="small" /> },
  { value: "ticketLabels", label: "Etiquetas", icon: <FormatListBulletedIcon fontSize="small" /> },
  { value: "funnelStage", label: "Embudo", icon: <ViewColumnIcon fontSize="small" /> },
  { value: "contactAction", label: "Botón de contacto", icon: <LinkIcon fontSize="small" /> },
  { value: "files", label: "Archivos multimedia", icon: <AttachFileIcon fontSize="small" /> },
  { value: "checkbox", label: "Casilla", icon: <CheckBoxOutlinedIcon fontSize="small" /> },
  { value: "url", label: "URL", icon: <LinkIcon fontSize="small" /> },
  { value: "phone", label: "Teléfono", icon: <PhoneIcon fontSize="small" /> },
  { value: "email", label: "Correo electrónico", icon: <AlternateEmailIcon fontSize="small" /> },
  { value: "relation", label: "Relación", icon: <LinkIcon fontSize="small" /> },
];

const defaultBases = [
  {
    id: "base-main",
    name: "Principal",
    groupBy: "",
    columns: [{ id: "name", name: "Nombre", type: "text" }],
    rows: [],
  },
];

const weekdays = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const padDatePart = value => String(value).padStart(2, "0");
const toDateInput = date =>
  `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`;

const startOfCalendarWeek = date => {
  const start = new Date(date);
  const mondayOffset = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - mondayOffset);
  start.setHours(0, 0, 0, 0);
  return start;
};

const getMonthCalendarDays = currentDate => {
  const first = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const last = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  const start = startOfCalendarWeek(first);
  const end = startOfCalendarWeek(last);
  end.setDate(end.getDate() + 6);
  const totalDays = Math.round((end.getTime() - start.getTime()) / 86400000) + 1;

  return Array.from({ length: totalDays }).map((_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });
};

const makeId = prefix => `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const getType = type => propertyTypes.find(item => item.value === type) || propertyTypes[0];

const getRowTitle = (base, row) => {
  const firstColumn = base?.columns?.[0];
  return row?.values?.[firstColumn?.id] || "Sin nombre";
};

const getMirrorRelationId = (sourceBaseId, sourceColumnId) =>
  `mirror-${sourceBaseId}-${sourceColumnId}`;

const contactDataFields = [
  { value: "name", label: "Nombre" },
  { value: "number", label: "Número de Whatsapp" },
  { value: "email", label: "Correo Electrónico" },
];

const contactActionOptions = [
  { value: "chat", label: "Chat", emoji: "💬" },
  { value: "reminders", label: "Recordatorios", emoji: "⏰" },
  { value: "agenda", label: "Agendamientos", emoji: "📅" },
  { value: "orders", label: "Pedidos", emoji: "🛒" },
  { value: "processes", label: "Procesos", emoji: "📃" },
  { value: "streaming", label: "Streaming", emoji: "📺" },
];

const getDefaultNewProperty = () => ({
  name: "",
  type: "text",
  relationBaseId: "",
  relationLimit: "none",
  bidirectional: true,
  currencySymbol: "$",
  currencyDecimals: 2,
  funnelId: "",
  contactAction: "reminders",
  contactField: "name",
  customFieldName: "",
  options: [
    { id: makeId("option"), label: "Nuevo", color: "#BAE6FD" },
    { id: makeId("option"), label: "En proceso", color: "#FEF3C7" },
    { id: makeId("option"), label: "Cerrado", color: "#BBF7D0" },
  ],
});

const normalizeFunnels = funnels => {
  if (!Array.isArray(funnels)) return [];

  return funnels.map(funnel => ({
    id: funnel.id,
    name: funnel.name || "Embudo",
    stages: Array.isArray(funnel.stages)
      ? funnel.stages.map(stage => ({
          id: stage.id,
          name: stage.name || "Etapa",
          color: stage.color || DEFAULT_OPTION_COLOR,
          position: Number(stage.position || 0),
        }))
      : [],
    ticketStages: (funnel.tickets || []).reduce((acc, item) => {
      acc[String(item.ticketId)] = item.stageId;
      return acc;
    }, {}),
  }));
};

const getReadableTextColor = color => {
  const hex = (color || "#ffffff").replace("#", "");
  const normalized =
    hex.length === 3
      ? hex.split("").map(char => `${char}${char}`).join("")
      : hex.padEnd(6, "f").slice(0, 6);
  const red = parseInt(normalized.slice(0, 2), 16);
  const green = parseInt(normalized.slice(2, 4), 16);
  const blue = parseInt(normalized.slice(4, 6), 16);
  const luminance = (red * 299 + green * 587 + blue * 114) / 1000;

  return luminance > 150 ? "#243040" : "#ffffff";
};

const normalizeOption = (option, index = 0) => {
  if (typeof option === "string") {
    return {
      id: option.toLowerCase().replace(/\s+/g, "-") || makeId("option"),
      label: option,
      color: optionPalette[index % optionPalette.length],
    };
  }

  return {
    id: option?.id || makeId("option"),
    label: option?.label || "Opción",
    color: option?.color || optionPalette[index % optionPalette.length],
  };
};

const normalizeColumn = column => ({
  ...column,
  relationLimit: column.relationLimit || "none",
  bidirectional: Boolean(column.bidirectional),
  options: ["select", "multiSelect", "status"].includes(column.type)
    ? (column.options || []).map(normalizeOption)
    : column.options,
});

const getEmptyValueForColumn = column => {
  if (column.type === "checkbox") return false;
  if (column.type === "multiSelect" || column.type === "relation") return [];
  if (["select", "status"].includes(column.type)) return "";
  return "";
};

const buildEmptyRowValues = columns =>
  columns.reduce(
    (acc, column) => ({
      ...acc,
      [column.id]: getEmptyValueForColumn(column),
    }),
    {}
  );

const buildEmptyRow = columns => ({
  id: makeId("row"),
  position: 0,
  values: buildEmptyRowValues(columns),
});

const ensureBaseRows = base => {
  const rows = Array.isArray(base.rows)
    ? base.rows.map((row, index) => ({ ...row, position: Number(row.position ?? index) }))
    : [];

  return rows.length ? rows : [buildEmptyRow(base.columns)];
};

const normalizeBase = base => {
  const columns = Array.isArray(base.columns) && base.columns.length
    ? base.columns.map(normalizeColumn)
    : defaultBases[0].columns;
  const nextBase = {
    ...base,
    id: base.id || makeId("base"),
    name: base.name || "Base sin nombre",
    groupBy: base.groupBy || "",
    columns,
  };

  return {
    ...nextBase,
    rows: ensureBaseRows(nextBase),
  };
};

const normalizeState = value => {
  if (Array.isArray(value?.bases)) {
    const bases = value.bases.map(normalizeBase);
    return {
      bases,
      activeBaseId: bases.some(base => base.id === value.activeBaseId)
        ? value.activeBaseId
        : bases[0]?.id || "",
    };
  }

  const bases = defaultBases.map(normalizeBase);
  return { bases, activeBaseId: bases[0].id };
};

const useStyles = makeStyles(theme => ({
  page: {
    minHeight: "calc(100vh - 48px)",
    backgroundColor: "#f7f7f5",
    padding: theme.spacing(3),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1.5),
    },
  },
  header: {
    alignItems: "flex-start",
    display: "flex",
    gap: theme.spacing(2),
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  eyebrow: {
    color: "#6b6f76",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0,
    marginBottom: theme.spacing(0.5),
    textTransform: "uppercase",
  },
  title: {
    color: "#222222",
    fontSize: 28,
    fontWeight: 700,
    lineHeight: 1.2,
  },
  subtitle: {
    color: "#6b6f76",
    marginTop: theme.spacing(0.5),
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  sheetConfigFields: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 140px",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  sheetConfigStatus: {
    marginTop: theme.spacing(1),
    minHeight: 20,
  },
  workspace: {
    border: "1px solid #e5e2dc",
    borderRadius: 8,
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    overflow: "hidden",
  },
  toolbar: {
    alignItems: "center",
    backgroundColor: "#ffffff",
    borderBottom: "1px solid #e5e2dc",
    display: "grid",
    gridTemplateColumns: "minmax(240px, 1fr) auto minmax(240px, 1fr)",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 1.5),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  toolbarLeft: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  toolbarRight: {
    alignItems: "center",
    display: "flex",
    justifyContent: "flex-end",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "flex-start",
    },
  },
  baseControl: {
    minWidth: 220,
  },
  baseSelectorGroup: {
    alignItems: "center",
    display: "flex",
    gap: theme.spacing(0.5),
  },
  baseActionButton: {
    flex: "0 0 auto",
  },
  groupingControl: {
    minWidth: 210,
  },
  tabs: {
    justifySelf: "center",
    minHeight: 38,
    "& .MuiTab-root": {
      minHeight: 38,
      textTransform: "none",
    },
  },
  filtersBar: {
    alignItems: "center",
    backgroundColor: "#ffffff",
    borderBottom: "1px solid #e5e2dc",
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
  },
  filterControl: {
    minWidth: 190,
    maxWidth: 240,
  },
  clearFiltersButton: {
    marginLeft: "auto",
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0,
    },
  },
  tableWrap: {
    backgroundColor: "#ffffff",
    overflow: "auto",
  },
  table: {
    minWidth: 980,
  },
  tableHeadCell: {
    backgroundColor: "#fbfbfa",
    borderBottom: "1px solid #e5e2dc",
    color: "#555b62",
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  tableHeaderContent: {
    alignItems: "center",
    display: "inline-flex",
    gap: theme.spacing(0.75),
    "& $hoverActions": {
      opacity: 0,
      pointerEvents: "none",
      transition: "opacity 120ms ease",
    },
    "&:hover $hoverActions": {
      opacity: 1,
      pointerEvents: "auto",
    },
  },
  columnHeaderCell: {
    "& $hoverActions": {
      opacity: 0,
      pointerEvents: "none",
      transition: "opacity 120ms ease",
    },
    "&:hover $hoverActions": {
      opacity: 1,
      pointerEvents: "auto",
    },
  },
  rowTitleCell: {
    fontWeight: 600,
    minWidth: 220,
  },
  cellInput: {
    minWidth: 160,
    "& .MuiOutlinedInput-input": {
      padding: theme.spacing(1),
    },
  },
  linkedInput: {
    minWidth: 190,
  },
  fileCell: {
    minWidth: 220,
    width: 260,
  },
  fileUploadLabel: {
    display: "block",
    width: "100%",
  },
  fileButton: {
    height: 40,
    justifyContent: "center",
    textTransform: "none",
    width: "100%",
  },
  filePreviewButton: {
    alignItems: "center",
    border: "1px solid #e5e2dc",
    borderRadius: 6,
    backgroundColor: "#fbfbfa",
    color: theme.palette.text.primary,
    cursor: "pointer",
    display: "flex",
    gap: theme.spacing(1),
    height: 40,
    minWidth: 0,
    padding: theme.spacing(0.5, 5.5, 0.5, 1),
    position: "relative",
    textAlign: "left",
    width: "100%",
    "&:hover": {
      backgroundColor: "#f1f1ef",
    },
  },
  fileReplaceLabel: {
    position: "absolute",
    right: 4,
    top: "50%",
    transform: "translateY(-50%)",
  },
  fileReplaceButton: {
    border: "1px solid #e5e2dc",
    flex: "none",
    height: 32,
    width: 32,
  },
  filePreviewThumb: {
    borderRadius: 4,
    flex: "none",
    height: 30,
    objectFit: "cover",
    width: 42,
  },
  filePreviewIcon: {
    alignItems: "center",
    backgroundColor: "#eef2f7",
    borderRadius: 4,
    color: "#5f6368",
    display: "flex",
    flex: "none",
    height: 30,
    justifyContent: "center",
    width: 42,
  },
  filePreviewName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  filePreviewImage: {
    display: "block",
    maxHeight: "70vh",
    maxWidth: "100%",
    objectFit: "contain",
  },
  filePreviewFrame: {
    border: 0,
    height: "70vh",
    width: "100%",
  },
  filePreviewVideo: {
    display: "block",
    maxHeight: "70vh",
    width: "100%",
  },
  selectCell: {
    minWidth: 180,
    width: "100%",
    "& .MuiSelect-root": {
      alignItems: "center",
      display: "flex",
      minHeight: 32,
      width: "100%",
    },
  },
  checkboxCell: {
    display: "flex",
    justifyContent: "center",
  },
  rowActions: {
    textAlign: "right",
    width: 92,
  },
  dragCell: {
    width: 72,
  },
  hoverActions: {},
  rowHoverActions: {
    display: "inline-flex",
    opacity: 0,
    pointerEvents: "none",
    transition: "opacity 120ms ease",
  },
  tableRow: {
    "&:hover $rowHoverActions": {
      opacity: 1,
      pointerEvents: "auto",
    },
  },
  rowDragHandle: {
    cursor: "grab",
    padding: 4,
    "&:active": {
      cursor: "grabbing",
    },
  },
  optionPill: {
    borderRadius: 999,
    fontWeight: 700,
    height: 24,
  },
  optionDot: {
    borderRadius: 999,
    display: "inline-block",
    height: 10,
    marginRight: 8,
    width: 10,
  },
  multiValue: {
    alignItems: "center",
    display: "flex",
    flexWrap: "nowrap",
    gap: 4,
    minHeight: 24,
    width: "100%",
    overflow: "hidden",
  },
  emptySelectValue: {
    color: theme.palette.text.secondary,
  },
  typeSelectValue: {
    alignItems: "center",
    display: "inline-flex",
    gap: theme.spacing(1),
  },
  kanban: {
    backgroundColor: "#fbfbfa",
    display: "grid",
    gap: theme.spacing(1.5),
    gridAutoColumns: "minmax(280px, 1fr)",
    gridAutoFlow: "column",
    overflowX: "auto",
    padding: theme.spacing(1.5),
    ...theme.scrollbarStyles,
  },
  kanbanColumn: {
    backgroundColor: "#f1f1ef",
    border: "1px solid #e5e2dc",
    borderRadius: 8,
    minHeight: 420,
    padding: theme.spacing(1),
    position: "relative",
  },
  columnDraggingOver: {
    boxShadow: `0 8px 18px rgba(0, 0, 0, 0.14)`,
    "&:before": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: 999,
      boxShadow: `0 0 0 4px ${theme.palette.primary.main}22`,
      content: '""',
      position: "absolute",
      top: 8,
      bottom: 8,
      width: 6,
      zIndex: 3,
    },
  },
  columnDropBefore: {
    "&:before": { left: 6 },
  },
  columnDropAfter: {
    "&:before": { right: 6 },
  },
  kanbanTitle: {
    alignItems: "center",
    display: "flex",
    fontWeight: 700,
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  kanbanCount: {
    backgroundColor: "#ffffff",
    border: "1px solid #e5e2dc",
    borderRadius: 999,
    color: "#6b6f76",
    fontSize: 12,
    padding: "1px 8px",
  },
  kanbanTotal: {
    marginLeft: "auto",
    maxWidth: 150,
    "& .MuiChip-label": {
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
  card: {
    backgroundColor: "#ffffff",
    border: "1px solid #e5e2dc",
    borderRadius: 8,
    cursor: "grab",
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1.25),
    "&:active": {
      cursor: "grabbing",
    },
  },
  calendarToolbar: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    justifyContent: "center",
    padding: theme.spacing(1.5),
  },
  calendarDateControl: {
    minWidth: 160,
  },
  calendar: {
    backgroundColor: "#fbfbfa",
    display: "grid",
    gap: theme.spacing(1),
    gridTemplateColumns: "repeat(7, minmax(160px, 1fr))",
    overflowX: "auto",
    padding: theme.spacing(1.5),
    ...theme.scrollbarStyles,
  },
  calendarDay: {
    backgroundColor: "#ffffff",
    border: "1px solid #e5e2dc",
    borderRadius: 8,
    minHeight: 150,
    padding: theme.spacing(1),
  },
  calendarDayMuted: {
    backgroundColor: "#f7f7f5",
  },
  calendarDayHeader: {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  calendarCard: {
    backgroundColor: "#fbfbfa",
    border: "1px solid #e5e2dc",
    borderRadius: 6,
    marginBottom: theme.spacing(0.75),
    padding: theme.spacing(0.75),
  },
  calendarCardTitle: {
    fontWeight: 700,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  calendarCardMeta: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    marginTop: 2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  rowEditorContent: {
    display: "grid",
    gap: theme.spacing(2),
  },
  rowEditorField: {
    display: "grid",
    gap: theme.spacing(0.75),
  },
  rowEditorLabel: {
    alignItems: "center",
    color: theme.palette.text.secondary,
    display: "inline-flex",
    fontSize: 12,
    fontWeight: 700,
    gap: theme.spacing(0.75),
  },
  cardDropBefore: {
    boxShadow: `0 -3px 0 ${theme.palette.primary.main}, 0 4px 12px rgba(0, 0, 0, 0.10)`,
  },
  cardDropAfter: {
    boxShadow: `0 3px 0 ${theme.palette.primary.main}, 0 4px 12px rgba(0, 0, 0, 0.10)`,
  },
  tableRowDropBefore: {
    boxShadow: `0 -3px 0 ${theme.palette.primary.main}`,
  },
  tableRowDropAfter: {
    boxShadow: `0 3px 0 ${theme.palette.primary.main}`,
  },
  tableColumnDropBefore: {
    boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
  },
  tableColumnDropAfter: {
    boxShadow: `inset -3px 0 0 ${theme.palette.primary.main}`,
  },
  cardTitle: {
    alignItems: "center",
    display: "flex",
    fontWeight: 700,
    gap: theme.spacing(0.75),
    lineHeight: 1.25,
    marginBottom: theme.spacing(1),
  },
  cardMeta: {
    display: "grid",
    gap: theme.spacing(0.75),
  },
  metaLine: {
    alignItems: "center",
    color: "#5f6368",
    display: "flex",
    fontSize: 12,
    gap: theme.spacing(0.75),
    minWidth: 0,
  },
  contactCell: {
    minWidth: 220,
    "& .MuiOutlinedInput-root": {
      padding: "3px 8px",
    },
    "& .MuiAvatar-root": {
      flex: "none",
      marginRight: theme.spacing(0.75),
    },
    "& .MuiAutocomplete-inputRoot[class*='MuiOutlinedInput-root'] .MuiAutocomplete-input": {
      padding: "5px 4px",
    },
  },
  contactValue: {
    alignItems: "center",
    display: "inline-flex",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  contactAvatar: {
    height: 28,
    width: 28,
  },
  contactName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  contactOption: {
    alignItems: "center",
    display: "flex",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  labelCell: {
    minWidth: 260,
  },
  labelSelectValue: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    minHeight: 24,
  },
  labelChip: {
    height: 22,
    maxWidth: "100%",
    "& .MuiChip-label": {
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
  actionButton: {
    height: 40,
    minWidth: 154,
    justifyContent: "center",
    paddingBottom: 0,
    paddingTop: 0,
    textAlign: "center",
    textTransform: "none",
    whiteSpace: "nowrap",
    width: "100%",
  },
  actionEmoji: {
    fontSize: 18,
    lineHeight: 1,
    marginRight: theme.spacing(1),
  },
  emptyState: {
    alignItems: "center",
    color: "#6b6f76",
    display: "flex",
    justifyContent: "center",
    minHeight: 220,
    padding: theme.spacing(3),
    textAlign: "center",
  },
  dialogGrid: {
    display: "grid",
    gap: theme.spacing(2),
    paddingTop: theme.spacing(1),
  },
  typeOption: {
    alignItems: "center",
    display: "inline-flex",
    gap: theme.spacing(1),
  },
  optionEditor: {
    display: "grid",
    gap: theme.spacing(1),
  },
  relationSettings: {
    display: "grid",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.background.default,
  },
  optionRow: {
    alignItems: "center",
    display: "grid",
    gap: theme.spacing(1),
    gridTemplateColumns: "34px minmax(0, 1fr) 34px",
  },
  colorButton: {
    border: "1px solid rgba(0,0,0,0.12)",
    borderRadius: 8,
    height: 30,
    minWidth: 30,
  },
  dragIcon: {
    color: theme.palette.text.secondary,
    flex: "none",
  },
}));

const WorkspaceData = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = React.useContext(AuthContext);
  const hideResellerControls = Boolean(user?.company?.resellerBranding);
  const saveTimerRef = useRef(null);
  const contactSearchTimerRef = useRef(null);
  const userSearchTimerRef = useRef(null);
  const fetchingContactIdsRef = useRef(new Set());
  const fetchingUserIdsRef = useRef(new Set());
  const fetchingTicketContactIdsRef = useRef(new Set());
  const fetchingLabelsConnectionIdsRef = useRef(new Set());
  const [state, setState] = useState(() => normalizeState(null));
  const [loading, setLoading] = useState(true);
  const [hydrated, setHydrated] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [funnels, setFunnels] = useState([]);
  const [customFieldNames, setCustomFieldNames] = useState([]);
  const [contactTicketsByContactId, setContactTicketsByContactId] = useState({});
  const [labelsByConnectionId, setLabelsByConnectionId] = useState({});
  const [view, setView] = useState("table");
  const [calendarDate, setCalendarDate] = useState(() => new Date());
  const [calendarColumnId, setCalendarColumnId] = useState("");
  const [rowEditorId, setRowEditorId] = useState("");
  const [rowFilters, setRowFilters] = useState({});
  const [propertyDialogOpen, setPropertyDialogOpen] = useState(false);
  const [baseDialogOpen, setBaseDialogOpen] = useState(false);
  const [newBaseName, setNewBaseName] = useState("");
  const [editingBaseId, setEditingBaseId] = useState("");
  const [draggingColumnId, setDraggingColumnId] = useState("");
  const [columnDropId, setColumnDropId] = useState("");
  const [columnDropPosition, setColumnDropPosition] = useState("");
  const [draggingPropertyColumnId, setDraggingPropertyColumnId] = useState("");
  const [propertyColumnDropId, setPropertyColumnDropId] = useState("");
  const [propertyColumnDropPosition, setPropertyColumnDropPosition] = useState("");
  const [draggingRowId, setDraggingRowId] = useState("");
  const [rowDropTarget, setRowDropTarget] = useState(null);
  const [insertMenuAnchor, setInsertMenuAnchor] = useState(null);
  const [insertMenuColumnId, setInsertMenuColumnId] = useState("");
  const [pendingInsertColumn, setPendingInsertColumn] = useState(null);
  const [editPropertyDialogOpen, setEditPropertyDialogOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [deletingRow, setDeletingRow] = useState(null);
  const [deletingColumn, setDeletingColumn] = useState(null);
  const [deletingBase, setDeletingBase] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [contactActionModal, setContactActionModal] = useState({
    action: "",
    ticket: null,
  });
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetBaseId, setSheetBaseId] = useState(NO_WORKSPACE_BASE);
  const [sheetSpreadsheetId, setSheetSpreadsheetId] = useState("");
  const [sheetName, setSheetName] = useState("");
  const [sheetLoading, setSheetLoading] = useState(false);
  const [sheetSaving, setSheetSaving] = useState(false);
  const [sheetSyncing, setSheetSyncing] = useState(false);
  const [sheetStatus, setSheetStatus] = useState("");
  const [newProperty, setNewProperty] = useState(() => getDefaultNewProperty());
  const availablePropertyTypes = useMemo(
    () =>
      hideResellerControls
        ? propertyTypes.filter(
            type => !["person", "ticketLabels", "funnelStage"].includes(type.value)
          )
        : propertyTypes,
    [hideResellerControls]
  );
  const availableContactActionOptions = useMemo(
    () =>
      hideResellerControls
        ? contactActionOptions.filter(
            option => !["chat", "reminders"].includes(option.value)
          )
        : contactActionOptions,
    [hideResellerControls]
  );

  useEffect(() => {
    let isMounted = true;

    const loadWorkspaceData = async () => {
      setLoading(true);

      try {
        const { data } = await api.get("/workspace-data");
        if (isMounted) {
          setState(normalizeState(data?.data));
        }
      } catch (err) {
        toastError(err);
      } finally {
        if (isMounted) {
          setLoading(false);
          setHydrated(true);
        }
      }
    };

    loadWorkspaceData();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!hydrated) return undefined;

    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    saveTimerRef.current = setTimeout(async () => {
      try {
        await api.put("/workspace-data", { data: state });
      } catch (err) {
        toastError(err);
      }
    }, 500);

    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, [hydrated, state]);

  const mergeContacts = useCallback(nextContacts => {
    setContacts(prevState => {
      const byId = new Map(prevState.map(contact => [String(contact.id), contact]));
      (nextContacts || []).forEach(contact => {
        if (contact?.id) byId.set(String(contact.id), contact);
      });
      return Array.from(byId.values()).sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""))
      );
    });
  }, []);

  const fetchContacts = useCallback(async (searchParam = "") => {
    try {
      const { data } = await api.get("/contacts", {
        params: { searchParam, pageNumber: 1, rowsPerPage: 50 },
      });
      mergeContacts(data.contacts || []);
    } catch (err) {
      toastError(err);
    }
  }, [mergeContacts]);

  const scheduleContactSearch = useCallback(searchParam => {
    if (contactSearchTimerRef.current) {
      clearTimeout(contactSearchTimerRef.current);
    }

    contactSearchTimerRef.current = setTimeout(() => {
      fetchContacts(searchParam);
    }, 300);
  }, [fetchContacts]);

  const fetchContactById = useCallback(async contactId => {
    if (!contactId) return;
    const normalizedContactId = String(contactId);
    if (fetchingContactIdsRef.current.has(normalizedContactId)) return;
    fetchingContactIdsRef.current.add(normalizedContactId);

    try {
      const { data } = await api.get(`/contacts/${normalizedContactId}`);
      mergeContacts([data]);
    } catch (err) {
      toastError(err);
    } finally {
      fetchingContactIdsRef.current.delete(normalizedContactId);
    }
  }, [mergeContacts]);

  const mergeUsers = useCallback(nextUsers => {
    setUsers(prevState => {
      const byId = new Map(prevState.map(user => [String(user.id), user]));
      (nextUsers || []).forEach(user => {
        if (user?.id) byId.set(String(user.id), user);
      });
      return Array.from(byId.values()).sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""))
      );
    });
  }, []);

  const fetchUsers = useCallback(async (searchParam = "") => {
    try {
      const { data } = await api.get("/users", {
        params: { searchParam, pageNumber: 1 },
      });
      mergeUsers(data.users || []);
    } catch (err) {
      toastError(err);
    }
  }, [mergeUsers]);

  const fetchUserById = useCallback(async userId => {
    if (!userId) return;
    const normalizedUserId = String(userId);
    if (fetchingUserIdsRef.current.has(normalizedUserId)) return;
    fetchingUserIdsRef.current.add(normalizedUserId);

    try {
      const { data } = await api.get(`/users/${normalizedUserId}`);
      mergeUsers([data]);
    } catch (err) {
      toastError(err);
    } finally {
      fetchingUserIdsRef.current.delete(normalizedUserId);
    }
  }, [mergeUsers]);

  const scheduleUserSearch = useCallback(searchParam => {
    if (userSearchTimerRef.current) {
      clearTimeout(userSearchTimerRef.current);
    }

    userSearchTimerRef.current = setTimeout(() => {
      fetchUsers(searchParam);
    }, 300);
  }, [fetchUsers]);

  const fetchFunnels = useCallback(async () => {
    try {
      const { data } = await api.get("/funnels");
      setFunnels(normalizeFunnels(data.funnels || []));
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    fetchFunnels();
  }, [fetchFunnels]);

  useEffect(() => {
    fetchContacts();
    fetchUsers();

    return () => {
      if (contactSearchTimerRef.current) {
        clearTimeout(contactSearchTimerRef.current);
      }
      if (userSearchTimerRef.current) {
        clearTimeout(userSearchTimerRef.current);
      }
    };
  }, [fetchContacts, fetchUsers]);

  const fetchCustomFieldNames = useCallback(async () => {
    try {
      const { data } = await api.get("/contacts/custom-fields/names");
      setCustomFieldNames(data.fields || []);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    fetchCustomFieldNames();
  }, [fetchCustomFieldNames]);

  useEffect(() => {
    const selectedContactIds = new Set();
    state.bases.forEach(base => {
      const contactColumns = base.columns.filter(column => column.type === "contact");
      if (!contactColumns.length) return;

      base.rows.forEach(row => {
        contactColumns.forEach(column => {
          const contactId = row.values?.[column.id];
          if (contactId) selectedContactIds.add(String(contactId));
        });
      });
    });

    const loadedContactIds = new Set(contacts.map(contact => String(contact.id)));
    selectedContactIds.forEach(async contactId => {
      const currentContact = contacts.find(contact => String(contact.id) === contactId);
      if (loadedContactIds.has(contactId) && Array.isArray(currentContact?.extraInfo)) {
        return;
      }

      fetchContactById(contactId);
    });
  }, [contacts, fetchContactById, state.bases]);

  useEffect(() => {
    const selectedUserIds = new Set();
    state.bases.forEach(base => {
      const personColumns = base.columns.filter(column => column.type === "person");
      if (!personColumns.length) return;

      base.rows.forEach(row => {
        personColumns.forEach(column => {
          const userId = row.values?.[column.id];
          if (userId) selectedUserIds.add(String(userId));
        });
      });
    });

    const loadedUserIds = new Set(users.map(user => String(user.id)));
    selectedUserIds.forEach(userId => {
      if (loadedUserIds.has(userId)) return;
      fetchUserById(userId);
    });
  }, [fetchUserById, state.bases, users]);

  const mergeContactTicket = useCallback((contactId, ticket) => {
    setContactTicketsByContactId(prevState => ({
      ...prevState,
      [String(contactId)]: ticket || null,
    }));
  }, []);

  const fetchContactTicket = useCallback(async contactId => {
    if (!contactId) return;
    const normalizedContactId = String(contactId);
    if (fetchingTicketContactIdsRef.current.has(normalizedContactId)) return;
    fetchingTicketContactIdsRef.current.add(normalizedContactId);

    try {
      const { data } = await api.get(
        `/workspace-data/contacts/${normalizedContactId}/ticket`
      );
      mergeContactTicket(normalizedContactId, data.ticket || null);
    } catch (err) {
      toastError(err);
    } finally {
      fetchingTicketContactIdsRef.current.delete(normalizedContactId);
    }
  }, [mergeContactTicket]);

  const fetchLabelsForConnection = useCallback(async connectionId => {
    const normalizedConnectionId = String(connectionId || "");
    if (!normalizedConnectionId) return;
    if (labelsByConnectionId[normalizedConnectionId]) return;
    if (fetchingLabelsConnectionIdsRef.current.has(normalizedConnectionId)) return;
    fetchingLabelsConnectionIdsRef.current.add(normalizedConnectionId);

    try {
      const { data } = await api.get("/labels", {
        params: { connectionId: normalizedConnectionId },
      });
      setLabelsByConnectionId(prevState => ({
        ...prevState,
        [normalizedConnectionId]: data || [],
      }));
    } catch (err) {
      toastError(err);
    } finally {
      fetchingLabelsConnectionIdsRef.current.delete(normalizedConnectionId);
    }
  }, [labelsByConnectionId]);

  useEffect(() => {
    const selectedContactIds = new Set();
    state.bases.forEach(base => {
      const hasTicketBackedColumn = base.columns.some(
        column => ["ticketLabels", "funnelStage", "contactAction"].includes(column.type)
      );
      if (!hasTicketBackedColumn) return;

      const contactColumns = base.columns.filter(column => column.type === "contact");
      if (!contactColumns.length) return;

      base.rows.forEach(row => {
        contactColumns.forEach(column => {
          const contactId = row.values?.[column.id];
          if (contactId) selectedContactIds.add(String(contactId));
        });
      });
    });

    selectedContactIds.forEach(contactId => {
      if (Object.prototype.hasOwnProperty.call(contactTicketsByContactId, contactId)) {
        return;
      }
      fetchContactTicket(contactId);
    });
  }, [contactTicketsByContactId, fetchContactTicket, state.bases]);

  useEffect(() => {
    Object.values(contactTicketsByContactId).forEach(ticket => {
      if (ticket?.connectionId) {
        fetchLabelsForConnection(ticket.connectionId);
      }
    });
  }, [contactTicketsByContactId, fetchLabelsForConnection]);

  const activeBase = useMemo(
    () => state.bases.find(base => base.id === state.activeBaseId) || state.bases[0],
    [state.bases, state.activeBaseId]
  );

  const groupedColumns = useMemo(
    () => activeBase.columns.filter(column => ["select", "status"].includes(column.type)),
    [activeBase]
  );

  const filterableColumns = useMemo(
    () =>
      activeBase.columns.filter(column =>
        [
          "select",
          "multiSelect",
          "status",
          "contact",
          "person",
          "ticketLabels",
          "funnelStage",
          "checkbox",
          "relation",
        ].includes(column.type)
      ),
    [activeBase]
  );

  const groupColumn = useMemo(
    () => activeBase.columns.find(column => column.id === activeBase.groupBy),
    [activeBase]
  );

  const calendarColumns = useMemo(
    () => activeBase.columns.filter(column => column.type === "date"),
    [activeBase]
  );

  const calendarColumn = useMemo(
    () =>
      calendarColumns.find(column => column.id === calendarColumnId) ||
      calendarColumns[0] ||
      null,
    [calendarColumnId, calendarColumns]
  );

  const calendarDays = useMemo(
    () => getMonthCalendarDays(calendarDate),
    [calendarDate]
  );

  const activeRows = useMemo(
    () => [...activeBase.rows].sort((a, b) => Number(a.position || 0) - Number(b.position || 0)),
    [activeBase.rows]
  );

  const rowEditorRow = useMemo(
    () => activeRows.find(row => row.id === rowEditorId) || null,
    [activeRows, rowEditorId]
  );

  useEffect(() => {
    setRowFilters({});
  }, [activeBase.id]);

  const loadSheetConfig = async baseId => {
    if (!baseId || baseId === NO_WORKSPACE_BASE) {
      setSheetSpreadsheetId("");
      setSheetName("");
      setSheetStatus("");
      return;
    }

    try {
      setSheetLoading(true);
      const { data } = await api.get("/workspace-data/sheets/config", {
        params: { baseId },
      });
      setSheetSpreadsheetId(data.config?.spreadsheetId || "");
      setSheetName(data.config?.sheetName || "");
      setSheetStatus("");
    } catch (err) {
      toastError(err);
    } finally {
      setSheetLoading(false);
    }
  };

  const openSheetModal = () => {
    const defaultBaseId = activeBase?.id || state.bases[0]?.id || NO_WORKSPACE_BASE;
    setSheetBaseId(defaultBaseId);
    setSheetOpen(true);
    loadSheetConfig(defaultBaseId);
  };

  const saveSheetConfig = async (showToast = true) => {
    if (sheetBaseId === NO_WORKSPACE_BASE) {
      toast.error("Selecciona una base.");
      return null;
    }

    if (!sheetSpreadsheetId.trim()) {
      toast.error("Ingresa el ID del documento.");
      return null;
    }

    if (!sheetName.trim()) {
      toast.error("Ingresa el nombre de la hoja.");
      return null;
    }

    setSheetSaving(true);
    try {
      const { data } = await api.put("/workspace-data/sheets/config", {
        baseId: sheetBaseId,
        spreadsheetId: sheetSpreadsheetId.trim(),
        sheetName: sheetName.trim(),
      });
      setSheetStatus("Configuracion guardada.");
      if (showToast) toast.success("Configuracion guardada.");
      return data;
    } catch (err) {
      toastError(err);
      return null;
    } finally {
      setSheetSaving(false);
    }
  };

  const syncWorkspaceBaseToSheet = async () => {
    const savedConfig = await saveSheetConfig(false);
    if (!savedConfig) return;

    try {
      setSheetSyncing(true);
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
      await api.put("/workspace-data", { data: state });
      const { data } = await api.post("/workspace-data/sheets/sync", {
        baseId: sheetBaseId,
      });
      toast.success("Registros enviados.");
      setSheetStatus(
        `${data.total || 0} registro(s) enviados en ${data.batches || 1} lote(s).`
      );
    } catch (err) {
      toastError(err);
    } finally {
      setSheetSyncing(false);
    }
  };

  const updateActiveBase = updater => {
    setState(prevState => ({
      ...prevState,
      bases: prevState.bases.map(base =>
        base.id === activeBase.id ? updater(base) : base
      ),
    }));
  };

  const updateCell = (rowId, columnId, value) => {
    const editedColumn = activeBase.columns.find(column => column.id === columnId);
    const mirrorColumnId = getMirrorRelationId(activeBase.id, columnId);

    setState(prevState => ({
      ...prevState,
      bases: prevState.bases.map(base => {
        if (base.id === activeBase.id) {
          return {
            ...base,
            rows: base.rows.map(row =>
              row.id === rowId
                ? { ...row, values: { ...row.values, [columnId]: value } }
                : row
            ),
          };
        }

        if (
          editedColumn?.type !== "relation" ||
          !editedColumn.bidirectional ||
          base.id !== editedColumn.relationBaseId
        ) {
          return base;
        }

        return {
          ...base,
          rows: base.rows.map(row => {
            const mirrorValues = activeBase.rows
              .filter(sourceRow => {
                if (sourceRow.id === rowId) {
                  return (value || []).includes(row.id);
                }
                return (sourceRow.values?.[columnId] || []).includes(row.id);
              })
              .map(sourceRow => sourceRow.id);

            return {
              ...row,
              values: {
                ...row.values,
                [mirrorColumnId]: mirrorValues,
              },
            };
          }),
        };
      }),
    }));
  };

  const getOption = (column, optionId) =>
    (column.options || []).find(option => String(option.id) === String(optionId));

  const selectedColumn = useMemo(
    () => activeBase.columns.find(column => column.id === insertMenuColumnId),
    [activeBase.columns, insertMenuColumnId]
  );

  const closeInsertMenu = () => {
    setInsertMenuAnchor(null);
    setInsertMenuColumnId("");
  };

  const openNewPropertyDialog = insertPosition => {
    setNewProperty(getDefaultNewProperty());
    setPendingInsertColumn(insertPosition || null);
    setPropertyDialogOpen(true);
  };

  const closeNewPropertyDialog = () => {
    setPropertyDialogOpen(false);
    setPendingInsertColumn(null);
  };

  const openInsertMenu = (event, columnId) => {
    event.stopPropagation();
    setInsertMenuAnchor(event.currentTarget);
    setInsertMenuColumnId(columnId);
  };

  const openNewBaseDialog = () => {
    setEditingBaseId("");
    setNewBaseName("");
    setBaseDialogOpen(true);
  };

  const openEditBaseDialog = () => {
    if (!activeBase?.id) return;
    setEditingBaseId(activeBase.id);
    setNewBaseName(activeBase.name || "");
    setBaseDialogOpen(true);
  };

  const closeBaseDialog = () => {
    setBaseDialogOpen(false);
    setEditingBaseId("");
    setNewBaseName("");
  };

  const saveBase = event => {
    event.preventDefault();
    const name = newBaseName.trim();
    if (!name) return;

    if (editingBaseId) {
      setState(prevState => ({
        ...prevState,
        bases: prevState.bases.map(base =>
          base.id === editingBaseId ? { ...base, name } : base
        ),
      }));
      closeBaseDialog();
      return;
    }

    const nameColumn = { id: makeId("column"), name: "Nombre", type: "text" };

    const base = normalizeBase({
      id: makeId("base"),
      name,
      groupBy: "",
      columns: [nameColumn],
      rows: [buildEmptyRow([nameColumn])],
    });

    setState(prevState => ({
      bases: [...prevState.bases, base],
      activeBaseId: base.id,
    }));
    closeBaseDialog();
  };

  const deleteBase = baseId => {
    setState(prevState => {
      if (prevState.bases.length <= 1) return prevState;

      const bases = prevState.bases.filter(base => base.id !== baseId);
      return {
        bases,
        activeBaseId:
          prevState.activeBaseId === baseId
            ? bases[0]?.id || ""
            : prevState.activeBaseId,
      };
    });
  };

  const requestDeleteBase = () => {
    if (state.bases.length <= 1) return;
    setDeletingBase(activeBase);
  };

  const confirmDeleteBase = () => {
    if (!deletingBase?.id) return;
    deleteBase(deletingBase.id);
    setDeletingBase(null);
  };

  const addRow = () => {
    const values = buildEmptyRowValues(activeBase.columns);

    updateActiveBase(base => ({
      ...base,
      rows: [{ id: makeId("row"), position: -1, values }, ...base.rows].map((row, index) => ({
        ...row,
        position: index,
      })),
    }));
  };

  const addRowBelow = targetRowId => {
    const values = buildEmptyRowValues(activeBase.columns);

    updateActiveBase(base => {
      const rows = [...base.rows].sort((a, b) => Number(a.position || 0) - Number(b.position || 0));
      const targetIndex = rows.findIndex(row => row.id === targetRowId);
      const insertIndex = targetIndex >= 0 ? targetIndex + 1 : rows.length;
      rows.splice(insertIndex, 0, { id: makeId("row"), position: insertIndex, values });

      return {
        ...base,
        rows: rows.map((row, index) => ({ ...row, position: index })),
      };
    });
  };

  const deleteRow = rowId => {
    updateActiveBase(base => ({
      ...base,
      rows: ensureBaseRows({
        ...base,
        rows: base.rows.filter(row => row.id !== rowId),
      }).map((row, index) => ({ ...row, position: index })),
    }));
  };

  const requestDeleteRow = rowId => {
    const row = activeBase.rows.find(item => item.id === rowId);
    setDeletingRow(row || { id: rowId });
  };

  const confirmDeleteRow = () => {
    if (!deletingRow?.id) return;
    deleteRow(deletingRow.id);
    setDeletingRow(null);
  };

  const deleteColumn = columnId => {
    if (activeBase.columns.length <= 1) return;
    const removedColumn = activeBase.columns.find(column => column.id === columnId);
    const mirrorColumnId = getMirrorRelationId(activeBase.id, columnId);

    setState(prevState => ({
      ...prevState,
      bases: prevState.bases.map(base => {
        if (base.id === activeBase.id) {
          return {
            ...base,
            groupBy: base.groupBy === columnId ? "" : base.groupBy,
            columns: base.columns.filter(column => column.id !== columnId),
            rows: base.rows.map(row => {
              const nextValues = { ...row.values };
              delete nextValues[columnId];
              return { ...row, values: nextValues };
            }),
          };
        }

        if (
          removedColumn?.type !== "relation" ||
          !removedColumn.bidirectional ||
          base.id !== removedColumn.relationBaseId
        ) {
          return base;
        }

        return {
          ...base,
          groupBy: base.groupBy === mirrorColumnId ? "" : base.groupBy,
          columns: base.columns.filter(column => column.id !== mirrorColumnId),
          rows: base.rows.map(row => {
            const nextValues = { ...row.values };
            delete nextValues[mirrorColumnId];
            return { ...row, values: nextValues };
          }),
        };
      }),
    }));
  };

  const requestDeleteColumn = columnId => {
    const column = activeBase.columns.find(item => item.id === columnId);
    setDeletingColumn(column || { id: columnId });
  };

  const confirmDeleteColumn = () => {
    if (!deletingColumn?.id) return;
    deleteColumn(deletingColumn.id);
    setDeletingColumn(null);
  };

  const insertColumnNear = side => {
    const targetColumn = selectedColumn;
    if (!targetColumn) return;

    openNewPropertyDialog({
      columnId: targetColumn.id,
      side,
    });
    closeInsertMenu();
  };

  const openEditProperty = column => {
    if (!column) return;
    setEditingProperty({
      ...column,
      options: ["select", "multiSelect", "status"].includes(column.type)
        ? (column.options || []).map(option => ({ ...option }))
        : [],
      relationBaseId: column.relationBaseId || state.bases.find(base => base.id !== activeBase.id)?.id || state.bases[0]?.id || "",
      relationLimit: column.relationLimit || "none",
      bidirectional: Boolean(column.bidirectional),
      currencySymbol: column.currencySymbol || "$",
      currencyDecimals: Number.isFinite(Number(column.currencyDecimals))
        ? Number(column.currencyDecimals)
        : 2,
      funnelId: column.funnelId || funnels[0]?.id || "",
      contactAction: column.contactAction || "reminders",
      contactField: column.contactField || "name",
      customFieldName: column.customFieldName || "",
    });
    setEditPropertyDialogOpen(true);
    closeInsertMenu();
  };

  const closeEditProperty = () => {
    setEditPropertyDialogOpen(false);
    setEditingProperty(null);
  };

  const updateEditingOption = (optionId, patch) => {
    setEditingProperty(prevState => ({
      ...prevState,
      options: prevState.options.map(option =>
        option.id === optionId ? { ...option, ...patch } : option
      ),
    }));
  };

  const addEditingOption = () => {
    setEditingProperty(prevState => ({
      ...prevState,
      options: [
        ...prevState.options,
        {
          id: makeId("option"),
          label: "Opción",
          color: optionPalette[prevState.options.length % optionPalette.length],
        },
      ],
    }));
  };

  const removeEditingOption = optionId => {
    setEditingProperty(prevState => ({
      ...prevState,
      options: prevState.options.filter(option => option.id !== optionId),
    }));
  };

  const saveEditProperty = () => {
    if (!editingProperty?.name?.trim()) return;
    const previousColumn = activeBase.columns.find(column => column.id === editingProperty.id);
    const previousMirrorId = getMirrorRelationId(activeBase.id, editingProperty.id);

    const nextColumn = {
      ...editingProperty,
      name: editingProperty.name.trim(),
      relationBaseId:
        editingProperty.type === "relation" ? editingProperty.relationBaseId : undefined,
      relationLimit:
        editingProperty.type === "relation" ? editingProperty.relationLimit || "none" : undefined,
      bidirectional:
        editingProperty.type === "relation" ? Boolean(editingProperty.bidirectional) : undefined,
      currencySymbol:
        editingProperty.type === "currency" ? editingProperty.currencySymbol || "$" : undefined,
      currencyDecimals:
        editingProperty.type === "currency"
          ? Math.max(
              0,
              Number.isFinite(Number(editingProperty.currencyDecimals))
                ? Number(editingProperty.currencyDecimals)
                : 2
            )
          : undefined,
      funnelId:
        editingProperty.type === "funnelStage"
          ? editingProperty.funnelId || funnels[0]?.id || ""
          : undefined,
      contactAction:
        editingProperty.type === "contactAction"
          ? editingProperty.contactAction || "reminders"
          : undefined,
      contactField:
        editingProperty.type === "contactData"
          ? editingProperty.contactField || "name"
          : undefined,
      customFieldName:
        editingProperty.type === "contactData" &&
        editingProperty.contactField === CONTACT_DATA_CUSTOM_FIELD
          ? editingProperty.customFieldName || customFieldNames[0] || ""
          : undefined,
      options: ["select", "multiSelect", "status"].includes(editingProperty.type)
        ? editingProperty.options
            .filter(option => option.label.trim())
            .map((option, index) => ({
              ...option,
              label: option.label.trim(),
              color: option.color || optionPalette[index % optionPalette.length],
            }))
        : undefined,
    };

    if (["select", "multiSelect", "status"].includes(nextColumn.type) && !nextColumn.options.length) {
      nextColumn.options = [{ id: makeId("option"), label: "Sin estado", color: DEFAULT_OPTION_COLOR }];
    }

    setState(prevState => ({
      ...prevState,
      bases: prevState.bases.map(base => {
        if (base.id === activeBase.id) {
          return {
            ...base,
            groupBy:
              base.groupBy === nextColumn.id && !["select", "status"].includes(nextColumn.type)
                ? ""
                : base.groupBy,
            columns: base.columns.map(column =>
              column.id === nextColumn.id ? nextColumn : column
            ),
            rows: base.rows.map(row => {
              const currentValue = row.values?.[nextColumn.id];
              return {
                ...row,
                values: {
                  ...row.values,
                  [nextColumn.id]:
                    currentValue === undefined ? getEmptyValueForColumn(nextColumn) : currentValue,
                },
              };
            }),
          };
        }

        const wasPreviousTarget =
          previousColumn?.type === "relation" &&
          previousColumn.bidirectional &&
          base.id === previousColumn.relationBaseId;
        const isNextTarget =
          nextColumn.type === "relation" &&
          nextColumn.bidirectional &&
          base.id === nextColumn.relationBaseId;

        if (!wasPreviousTarget && !isNextTarget) return base;

        const mirrorColumns = base.columns.filter(column => column.id !== previousMirrorId);
        const mirrorRows = base.rows.map(row => {
          const nextValues = { ...row.values };
          delete nextValues[previousMirrorId];
          return { ...row, values: nextValues };
        });

        if (!isNextTarget) {
          return {
            ...base,
            groupBy: base.groupBy === previousMirrorId ? "" : base.groupBy,
            columns: mirrorColumns,
            rows: mirrorRows,
          };
        }

        const mirrorColumn = {
          id: previousMirrorId,
          name: activeBase.name,
          type: "relation",
          relationBaseId: activeBase.id,
          relationLimit: "none",
          bidirectional: false,
          mirrorOf: nextColumn.id,
          sourceBaseId: activeBase.id,
        };

        return {
          ...base,
          columns: [...mirrorColumns, mirrorColumn],
          rows: mirrorRows.map(row => ({
            ...row,
            values: {
              ...row.values,
              [mirrorColumn.id]: activeBase.rows
                .filter(sourceRow => (sourceRow.values?.[nextColumn.id] || []).includes(row.id))
                .map(sourceRow => sourceRow.id),
            },
          })),
        };
      }),
    }));
    closeEditProperty();
  };

  const addProperty = () => {
    const name = newProperty.name.trim();
    if (!name) return;
    const defaultRelationBaseId =
      state.bases.find(base => base.id !== activeBase.id)?.id ||
      state.bases[0]?.id ||
      "";

    const column = {
      id: makeId("column"),
      name,
      type: newProperty.type,
      relationBaseId:
        newProperty.type === "relation"
          ? newProperty.relationBaseId || defaultRelationBaseId
          : undefined,
      relationLimit:
        newProperty.type === "relation" ? newProperty.relationLimit || "none" : undefined,
      bidirectional:
        newProperty.type === "relation" ? Boolean(newProperty.bidirectional) : undefined,
      currencySymbol:
        newProperty.type === "currency" ? newProperty.currencySymbol || "$" : undefined,
      currencyDecimals:
        newProperty.type === "currency"
          ? Math.max(
              0,
              Number.isFinite(Number(newProperty.currencyDecimals))
                ? Number(newProperty.currencyDecimals)
                : 2
            )
          : undefined,
      funnelId:
        newProperty.type === "funnelStage"
          ? newProperty.funnelId || funnels[0]?.id || ""
          : undefined,
      contactAction:
        newProperty.type === "contactAction"
          ? newProperty.contactAction || "reminders"
          : undefined,
      contactField:
        newProperty.type === "contactData" ? newProperty.contactField || "name" : undefined,
      customFieldName:
        newProperty.type === "contactData" &&
        newProperty.contactField === CONTACT_DATA_CUSTOM_FIELD
          ? newProperty.customFieldName || customFieldNames[0] || ""
          : undefined,
      options: ["select", "multiSelect", "status"].includes(newProperty.type)
        ? newProperty.options
            .filter(option => option.label.trim())
            .map((option, index) => ({
              ...option,
              id: option.id || makeId("option"),
              label: option.label.trim(),
              color: option.color || optionPalette[index % optionPalette.length],
            }))
        : undefined,
    };

    if (["select", "multiSelect", "status"].includes(column.type) && !column.options.length) {
      column.options = [{ id: makeId("option"), label: "Sin estado", color: DEFAULT_OPTION_COLOR }];
    }

    setState(prevState => ({
      ...prevState,
      bases: prevState.bases.map(base => {
        if (base.id === activeBase.id) {
          const columns = [...base.columns];
          const targetIndex = pendingInsertColumn?.columnId
            ? columns.findIndex(item => item.id === pendingInsertColumn.columnId)
            : -1;
          const insertIndex =
            targetIndex >= 0
              ? targetIndex + (pendingInsertColumn.side === "right" ? 1 : 0)
              : columns.length;

          columns.splice(insertIndex, 0, column);

          return {
            ...base,
            columns,
            groupBy: base.groupBy || (["select", "status"].includes(column.type) ? column.id : ""),
            rows: base.rows.map(row => ({
              ...row,
              values: {
                ...row.values,
                [column.id]: getEmptyValueForColumn(column),
              },
            })),
          };
        }

        if (column.type !== "relation" || !column.bidirectional || base.id !== column.relationBaseId) {
          return base;
        }

        const mirrorColumn = {
          id: getMirrorRelationId(activeBase.id, column.id),
          name: activeBase.name,
          type: "relation",
          relationBaseId: activeBase.id,
          relationLimit: "none",
          bidirectional: false,
          mirrorOf: column.id,
          sourceBaseId: activeBase.id,
        };

        return {
          ...base,
          columns: [...base.columns, mirrorColumn],
          rows: base.rows.map(row => ({
            ...row,
            values: { ...row.values, [mirrorColumn.id]: [] },
          })),
        };
      }),
    }));
    setNewProperty(getDefaultNewProperty());
    closeNewPropertyDialog();
  };

  const updateNewOption = (optionId, patch) => {
    setNewProperty(prevState => ({
      ...prevState,
      options: prevState.options.map(option =>
        option.id === optionId ? { ...option, ...patch } : option
      ),
    }));
  };

  const addNewOption = () => {
    setNewProperty(prevState => ({
      ...prevState,
      options: [
        ...prevState.options,
        {
          id: makeId("option"),
          label: "Opción",
          color: optionPalette[prevState.options.length % optionPalette.length],
        },
      ],
    }));
  };

  const removeNewOption = optionId => {
    setNewProperty(prevState => ({
      ...prevState,
      options: prevState.options.filter(option => option.id !== optionId),
    }));
  };

  const moveRowToGroup = ({ rowId, groupId, targetRowId, placement = "after" }) => {
    const groupValue = groupId === "__empty__" ? "" : groupId;

    updateActiveBase(base => {
      const rows = base.rows.map(row =>
        row.id === rowId
          ? { ...row, values: { ...row.values, [groupColumn.id]: groupValue } }
          : row
      );
      const nextRows = [...rows].sort((a, b) => Number(a.position || 0) - Number(b.position || 0));
      const sourceIndex = nextRows.findIndex(row => row.id === rowId);
      if (sourceIndex < 0) return base;

      const [movedRow] = nextRows.splice(sourceIndex, 1);
      let insertIndex = nextRows.length;

      if (targetRowId) {
        const targetIndex = nextRows.findIndex(row => row.id === targetRowId);
        if (targetIndex >= 0) insertIndex = targetIndex + (placement === "after" ? 1 : 0);
      }

      nextRows.splice(insertIndex, 0, movedRow);

      return {
        ...base,
        rows: nextRows.map((row, index) => ({ ...row, position: index })),
      };
    });
    setDraggingRowId("");
    setRowDropTarget(null);
  };

  const reorderRows = ({ rowId, targetRowId, placement = "after" }) => {
    updateActiveBase(base => {
      const nextRows = [...base.rows].sort((a, b) => Number(a.position || 0) - Number(b.position || 0));
      const sourceIndex = nextRows.findIndex(row => row.id === rowId);
      const targetIndex = nextRows.findIndex(row => row.id === targetRowId);

      if (sourceIndex < 0 || targetIndex < 0 || rowId === targetRowId) return base;

      const [movedRow] = nextRows.splice(sourceIndex, 1);
      const cleanTargetIndex = nextRows.findIndex(row => row.id === targetRowId);
      const insertIndex = cleanTargetIndex + (placement === "after" ? 1 : 0);
      nextRows.splice(insertIndex, 0, movedRow);

      return {
        ...base,
        rows: nextRows.map((row, index) => ({ ...row, position: index })),
      };
    });
    setDraggingRowId("");
    setRowDropTarget(null);
  };

  const handlePropertyColumnDragStart = columnId => event => {
    event.dataTransfer.setData(PROPERTY_COLUMN_DRAG_TYPE, String(columnId));
    event.dataTransfer.effectAllowed = "move";
    setDraggingPropertyColumnId(String(columnId));
  };

  const handlePropertyColumnDragEnd = () => {
    setDraggingPropertyColumnId("");
    setPropertyColumnDropId("");
    setPropertyColumnDropPosition("");
  };

  const handlePropertyColumnDragOver = columnId => event => {
    if (!draggingPropertyColumnId || draggingPropertyColumnId === columnId) return;

    const bounds = event.currentTarget.getBoundingClientRect();
    const placement = event.clientX > bounds.left + bounds.width / 2 ? "after" : "before";
    setPropertyColumnDropId(prevState =>
      prevState === String(columnId) ? prevState : String(columnId)
    );
    setPropertyColumnDropPosition(prevState =>
      prevState === placement ? prevState : placement
    );
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const handlePropertyColumnDrop = targetColumnId => event => {
    const draggedColumnId = event.dataTransfer.getData(PROPERTY_COLUMN_DRAG_TYPE);
    if (!draggedColumnId || draggedColumnId === targetColumnId) return;

    event.preventDefault();
    updateActiveBase(base => {
      const columns = [...base.columns];
      const sourceIndex = columns.findIndex(column => column.id === draggedColumnId);
      const targetIndex = columns.findIndex(column => column.id === targetColumnId);
      if (sourceIndex < 0 || targetIndex < 0) return base;

      const bounds = event.currentTarget.getBoundingClientRect();
      const shouldInsertAfter = event.clientX > bounds.left + bounds.width / 2;
      let insertIndex = targetIndex + (shouldInsertAfter ? 1 : 0);
      const [movedColumn] = columns.splice(sourceIndex, 1);
      if (sourceIndex < insertIndex) insertIndex -= 1;
      columns.splice(insertIndex, 0, movedColumn);

      return { ...base, columns };
    });
    handlePropertyColumnDragEnd();
  };

  const handleColumnDragStart = groupId => event => {
    event.dataTransfer.setData(COLUMN_DRAG_TYPE, String(groupId));
    event.dataTransfer.effectAllowed = "move";
    setDraggingColumnId(String(groupId));
  };

  const handleColumnDragEnd = () => {
    setDraggingColumnId("");
    setColumnDropId("");
    setColumnDropPosition("");
  };

  const handleColumnDragOver = groupId => event => {
    if (!draggingColumnId) {
      event.preventDefault();
      return;
    }

    if (String(groupId) !== String(draggingColumnId)) {
      const bounds = event.currentTarget.getBoundingClientRect();
      const nextPosition = event.clientX > bounds.left + bounds.width / 2 ? "after" : "before";
      setColumnDropId(prevState => (prevState === String(groupId) ? prevState : String(groupId)));
      setColumnDropPosition(prevState => (prevState === nextPosition ? prevState : nextPosition));
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const handleColumnDrop = targetGroup => event => {
    const draggedGroupId = event.dataTransfer.getData(COLUMN_DRAG_TYPE);
    if (draggedGroupId) {
      event.preventDefault();
      event.stopPropagation();

      if (!groupColumn || draggedGroupId === targetGroup.id) {
        handleColumnDragEnd();
        return;
      }

      updateActiveBase(base => ({
        ...base,
        columns: base.columns.map(column => {
          if (column.id !== groupColumn.id) return column;

          const options = [...(column.options || [])];
          const sourceIndex = options.findIndex(option => String(option.id) === String(draggedGroupId));
          const targetIndex = options.findIndex(option => String(option.id) === String(targetGroup.id));
          if (sourceIndex < 0 || targetIndex < 0) return column;

          const bounds = event.currentTarget.getBoundingClientRect();
          const shouldInsertAfter = event.clientX > bounds.left + bounds.width / 2;
          let insertIndex = targetIndex + (shouldInsertAfter ? 1 : 0);
          const [movedOption] = options.splice(sourceIndex, 1);
          if (sourceIndex < insertIndex) insertIndex -= 1;
          options.splice(insertIndex, 0, movedOption);
          return { ...column, options };
        }),
      }));
      handleColumnDragEnd();
      return;
    }

    const draggedRowId = event.dataTransfer.getData(ROW_DRAG_TYPE);
    if (draggedRowId) {
      event.preventDefault();
      moveRowToGroup({ rowId: draggedRowId, groupId: targetGroup.id });
    }
  };

  const renderOptionChip = (column, optionId) => {
    if (optionId === NO_SELECT_VALUE || !optionId) {
      return <span className={classes.emptySelectValue}>Sin selección</span>;
    }

    const option = getOption(column, optionId);
    if (!option) return null;

    return (
      <Chip
        key={option.id}
        className={classes.optionPill}
        size="small"
        label={option.label}
        style={{ backgroundColor: option.color, color: "#243040" }}
      />
    );
  };

  const renderLimitedChips = (items, renderItem, limit = 2, emptyLabel = "Sin selección") => {
    const visibleItems = (items || []).slice(0, limit);
    const hiddenCount = Math.max(0, (items || []).length - visibleItems.length);

    return (
      <span className={classes.multiValue}>
        {!visibleItems.length && (
          <span className={classes.emptySelectValue}>{emptyLabel}</span>
        )}
        {visibleItems.map(renderItem)}
        {hiddenCount > 0 && (
          <Chip
            className={classes.optionPill}
            size="small"
            label={`+${hiddenCount}`}
          />
        )}
      </span>
    );
  };

  const renderRelationValue = (column, selectedIds = []) => {
    const relatedBase = state.bases.find(base => base.id === column.relationBaseId);
    if (!relatedBase) return null;

    return (
      renderLimitedChips(selectedIds || [], rowId => {
          const row = relatedBase.rows.find(item => item.id === rowId);
          if (!row) return null;
          return <Chip key={rowId} size="small" label={getRowTitle(relatedBase, row)} />;
        }, 2, "Relacionar registro")
    );
  };

  const getContactById = contactId =>
    contacts.find(contact => String(contact.id) === String(contactId)) || null;

  const getUserById = userId =>
    users.find(user => String(user.id) === String(userId)) || null;

  const renderContactValue = contactId => {
    const contact = getContactById(contactId);

    if (!contact) {
      return <span className={classes.emptySelectValue}>Seleccionar contacto</span>;
    }

    return (
      <span className={classes.contactValue}>
        <Avatar src={contact.profilePicUrl} className={classes.contactAvatar} />
        <span className={classes.contactName}>{contact.name}</span>
      </span>
    );
  };

  const renderContactOption = contact => (
    <span className={classes.contactOption}>
      <Avatar src={contact.profilePicUrl} className={classes.contactAvatar} />
      <span className={classes.contactName}>{contact.name}</span>
    </span>
  );

  const renderUserValue = userId => {
    const user = getUserById(userId);

    if (!user) {
      return <span className={classes.emptySelectValue}>Seleccionar usuario</span>;
    }

    return (
      <span className={classes.contactValue}>
        <Avatar src={user.profilePicUrl} className={classes.contactAvatar}>
          {String(user.name || "?").slice(0, 1).toUpperCase()}
        </Avatar>
        <span className={classes.contactName}>{user.name}</span>
      </span>
    );
  };

  const renderUserOption = user => (
    <span className={classes.contactOption}>
      <Avatar src={user.profilePicUrl} className={classes.contactAvatar}>
        {String(user.name || "?").slice(0, 1).toUpperCase()}
      </Avatar>
      <span className={classes.contactName}>{user.name}</span>
    </span>
  );

  const getRowContactId = row => {
    const contactColumn = activeBase.columns.find(column => column.type === "contact");
    return contactColumn ? row.values?.[contactColumn.id] : "";
  };

  const getContactDataFieldLabel = column => {
    if (column.contactField === CONTACT_DATA_CUSTOM_FIELD) {
      return column.customFieldName || "Campo personalizado";
    }

    return (
      contactDataFields.find(field => field.value === column.contactField)?.label ||
      "Dato de contacto"
    );
  };

  const getContactDataValue = (contact, column) => {
    if (!contact) return "";

    if (column.contactField === CONTACT_DATA_CUSTOM_FIELD) {
      const field = (contact.extraInfo || []).find(
        info => String(info?.name || "") === String(column.customFieldName || "")
      );
      return field?.value || "";
    }

    return contact[column.contactField || "name"] || "";
  };

  const updateCachedContact = contact => {
    if (!contact?.id) return;
    mergeContacts([contact]);
  };

  const buildContactPayloadWithData = (contact, column, value) => {
    if (column.contactField !== CONTACT_DATA_CUSTOM_FIELD) {
      return { [column.contactField || "name"]: value };
    }

    const customFieldName = String(column.customFieldName || "").trim();
    const extraInfo = [...(contact.extraInfo || [])];
    const fieldIndex = extraInfo.findIndex(
      info => String(info?.name || "") === customFieldName
    );

    if (fieldIndex >= 0) {
      extraInfo[fieldIndex] = { ...extraInfo[fieldIndex], value };
    } else if (customFieldName) {
      extraInfo.push({ name: customFieldName, value });
    }

    return { extraInfo };
  };

  const buildContactUpdatePayload = (contact, column, value) => {
    const payload = {
      name: contact.name || "",
      email: contact.email || "",
      extraInfo: contact.extraInfo || [],
      ...buildContactPayloadWithData(contact, column, value),
    };

    if (contact.number || column.contactField === "number") {
      payload.number = column.contactField === "number" ? value : contact.number;
    }

    return payload;
  };

  const handleContactDataCommit = async (row, column, value) => {
    const contactId = getRowContactId(row);
    const contact = getContactById(contactId);
    if (!contact?.id) return;

    const previousContact = contact;
    const payload = buildContactUpdatePayload(contact, column, value);

    updateCachedContact({ ...contact, ...payload });

    try {
      const { data } = await api.put(`/contacts/${contact.id}`, payload);
      updateCachedContact(data);
      fetchCustomFieldNames();
    } catch (err) {
      updateCachedContact(previousContact);
      toastError(err);
    }
  };

  const renderContactDataCell = (row, column) => {
    const contactId = getRowContactId(row);
    const contact = getContactById(contactId);
    const value = getContactDataValue(contact, column);
    const disabled =
      !contact?.id ||
      (column.contactField === CONTACT_DATA_CUSTOM_FIELD && !column.customFieldName);

    return (
      <TextField
        className={classes.cellInput}
        label={getContactDataFieldLabel(column)}
        value={value}
        onChange={event => {
          if (!contact?.id) return;
          updateCachedContact({
            ...contact,
            ...buildContactPayloadWithData(contact, column, event.target.value),
          });
        }}
        onBlur={event => handleContactDataCommit(row, column, event.target.value)}
        variant="outlined"
        margin="dense"
        size="small"
        disabled={disabled}
      />
    );
  };

  const getTicketForRow = row => {
    const contactId = getRowContactId(row);
    return contactId ? contactTicketsByContactId[String(contactId)] : null;
  };

  const updateCachedTicket = ticket => {
    if (!ticket?.contactId) return;
    mergeContactTicket(ticket.contactId, ticket);
  };

  const handleTicketLabelsChange = async (row, labelIds) => {
    const ticket = getTicketForRow(row);
    if (!ticket?.id) return;

    const contactId = getRowContactId(row);
    const previousTicket = ticket;
    const labels = labelsByConnectionId[String(ticket.connectionId || "")] || [];
    const optimisticTicket = {
      ...ticket,
      labels: labelIds
        .map(labelId => labels.find(label => String(label.id) === String(labelId)))
        .filter(Boolean),
    };

    mergeContactTicket(contactId, optimisticTicket);

    try {
      const { data } = await api.put(
        `/workspace-data/tickets/${ticket.id}/labels`,
        { labelIds }
      );
      updateCachedTicket(data.ticket);
    } catch (err) {
      mergeContactTicket(contactId, previousTicket);
      toastError(err);
    }
  };

  const renderTicketLabelsValue = (selected, labels) => (
    <div className={classes.labelSelectValue}>
      {selected.length ? (
        labels
          .filter(label => selected.some(labelId => String(labelId) === String(label.id)))
          .map(label => (
            <Chip
              key={label.id}
              label={label.name}
              className={classes.labelChip}
              style={{
                backgroundColor: label.color,
                color: getReadableTextColor(label.color),
              }}
            />
          ))
      ) : (
        <span>&ZeroWidthSpace;</span>
      )}
    </div>
  );

  const getLabelsForTicket = ticket => {
    const loadedLabels = labelsByConnectionId[String(ticket?.connectionId || "")] || [];
    const byId = new Map(loadedLabels.map(label => [String(label.id), label]));
    (ticket?.labels || []).forEach(label => {
      if (label?.id) byId.set(String(label.id), label);
    });
    return Array.from(byId.values()).sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""))
    );
  };

  const renderTicketLabelsCell = row => {
    const contactId = getRowContactId(row);
    const ticket = getTicketForRow(row);
    const labels = getLabelsForTicket(ticket);
    const selectedLabelIds = (ticket?.labels || []).map(label => label.id);
    const disabled = !contactId || !ticket?.id;

    return (
      <FormControl
        className={classes.labelCell}
        margin="dense"
        size="small"
        variant="outlined"
        disabled={disabled}
      >
        <InputLabel>Etiquetas</InputLabel>
        <Select
          multiple
          value={selectedLabelIds}
          onOpen={() => {
            if (ticket?.connectionId) fetchLabelsForConnection(ticket.connectionId);
            if (contactId) fetchContactTicket(contactId);
          }}
          onChange={event => handleTicketLabelsChange(row, event.target.value)}
          label="Etiquetas"
          renderValue={selected => renderTicketLabelsValue(selected, labels)}
        >
          {labels.map(label => (
            <MenuItem key={label.id} value={label.id}>
              <Checkbox
                style={{ color: label.color }}
                checked={selectedLabelIds.some(
                  labelId => String(labelId) === String(label.id)
                )}
              />
              <ListItemText primary={label.name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const renderTypeValue = typeValue => {
    const type = getType(typeValue);

    return (
      <span className={classes.typeSelectValue}>
        {type.icon}
        {type.label}
      </span>
    );
  };

  const renderContactDataSettings = (property, setProperty) => {
    const customFieldValue = property.customFieldName || customFieldNames[0] || "";

    return (
      <div className={classes.relationSettings}>
        <FormControl variant="outlined" fullWidth>
          <InputLabel>Campo del contacto</InputLabel>
          <Select
            value={property.contactField || "name"}
            onChange={event =>
              setProperty(prevState => ({
                ...prevState,
                contactField: event.target.value,
                customFieldName:
                  event.target.value === CONTACT_DATA_CUSTOM_FIELD
                    ? prevState.customFieldName || customFieldNames[0] || ""
                    : "",
              }))
            }
            label="Campo del contacto"
          >
            {contactDataFields.map(field => (
              <MenuItem key={field.value} value={field.value}>
                {field.label}
              </MenuItem>
            ))}
            <MenuItem value={CONTACT_DATA_CUSTOM_FIELD}>Campo personalizado</MenuItem>
          </Select>
        </FormControl>
        {property.contactField === CONTACT_DATA_CUSTOM_FIELD && (
          <FormControl variant="outlined" fullWidth>
            <InputLabel>Campo personalizado</InputLabel>
            <Select
              value={customFieldValue || "__no_custom_fields__"}
              onOpen={fetchCustomFieldNames}
              onChange={event =>
                setProperty(prevState => ({
                  ...prevState,
                  customFieldName:
                    event.target.value === "__no_custom_fields__"
                      ? ""
                      : event.target.value,
                }))
              }
              label="Campo personalizado"
            >
              {customFieldNames.length ? (
                customFieldNames.map(fieldName => (
                  <MenuItem key={fieldName} value={fieldName}>
                    {fieldName}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="__no_custom_fields__">
                  Sin campos personalizados
                </MenuItem>
              )}
            </Select>
          </FormControl>
        )}
      </div>
    );
  };

  const renderFunnelStageSettings = (property, setProperty) => (
    <div className={classes.relationSettings}>
      <FormControl variant="outlined" fullWidth>
        <InputLabel>Embudo</InputLabel>
        <Select
          value={property.funnelId || funnels[0]?.id || LOADING_FUNNELS}
          onOpen={fetchFunnels}
          onChange={event =>
            setProperty(prevState => ({
              ...prevState,
              funnelId:
                event.target.value === LOADING_FUNNELS ? "" : event.target.value,
            }))
          }
          label="Embudo"
        >
          {funnels.length ? (
            funnels.map(funnel => (
              <MenuItem key={funnel.id} value={funnel.id}>
                {funnel.name}
              </MenuItem>
            ))
          ) : (
            <MenuItem value={LOADING_FUNNELS}>Cargando embudos</MenuItem>
          )}
        </Select>
      </FormControl>
    </div>
  );

  const renderContactActionSettings = (property, setProperty) => (
    <div className={classes.relationSettings}>
      <FormControl variant="outlined" fullWidth>
        <InputLabel>Acción</InputLabel>
        <Select
          value={
            availableContactActionOptions.some(
              option => option.value === property.contactAction
            )
              ? property.contactAction
              : availableContactActionOptions[0]?.value || "orders"
          }
          onChange={event =>
            setProperty(prevState => ({
              ...prevState,
              contactAction: event.target.value,
            }))
          }
          label="Acción"
          renderValue={selected => {
            const option =
              availableContactActionOptions.find(item => item.value === selected) ||
              availableContactActionOptions[0];
            return `${option.emoji} ${option.label}`;
          }}
        >
          {availableContactActionOptions.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.emoji} {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );

  const renderCurrencySettings = (property, setProperty) => (
    <div className={classes.relationSettings}>
      <TextField
        label="Símbolo de moneda"
        value={property.currencySymbol || ""}
        onChange={event =>
          setProperty(prevState => ({
            ...prevState,
            currencySymbol: event.target.value,
          }))
        }
        variant="outlined"
        fullWidth
      />
      <TextField
        label="Cantidad de decimales"
        type="number"
        value={Number.isFinite(Number(property.currencyDecimals)) ? property.currencyDecimals : 2}
        onChange={event =>
          setProperty(prevState => ({
            ...prevState,
            currencyDecimals: Math.max(0, Number(event.target.value || 0)),
          }))
        }
        variant="outlined"
        fullWidth
        inputProps={{ min: 0, step: 1 }}
      />
    </div>
  );

  const getContactActionOption = action =>
    contactActionOptions.find(option => option.value === action) ||
    contactActionOptions[0];

  const handleContactAction = (row, column) => {
    const ticket = getTicketForRow(row);
    if (!ticket?.id) return;

    const option = getContactActionOption(column.contactAction);
    if (option.value === "chat") {
      history.push(`/tickets/${ticket.id}`);
      return;
    }

    setContactActionModal({
      action: option.value,
      ticket,
    });
  };

  const addCalendarRow = day => {
    if (!calendarColumn) return;
    const values = {
      ...buildEmptyRowValues(activeBase.columns),
      [calendarColumn.id]: toDateInput(day),
    };

    updateActiveBase(base => ({
      ...base,
      rows: [{ id: makeId("row"), position: -1, values }, ...base.rows].map((row, index) => ({
        ...row,
        position: index,
      })),
    }));
  };

  const closeContactActionModal = () => {
    const contactId = contactActionModal.ticket?.contactId;

    setContactActionModal({
      action: "",
      ticket: null,
    });

    if (contactId) {
      fetchContactTicket(contactId);
    }
  };

  const renderContactActionCell = (row, column) => {
    const contactId = getRowContactId(row);
    const ticket = getTicketForRow(row);
    const option = getContactActionOption(column.contactAction);
    const disabled = !contactId || !ticket?.id;

    return (
      <Tooltip
        title={
          disabled
            ? "Selecciona un contacto con ticket"
            : option.label
        }
      >
        <span>
          <Button
            className={classes.actionButton}
            variant="outlined"
            size="small"
            disabled={disabled}
            onClick={() => handleContactAction(row, column)}
          >
            <span className={classes.actionEmoji}>{option.emoji}</span>
            {option.label}
          </Button>
        </span>
      </Tooltip>
    );
  };

  const renderKanbanCardTitle = row => {
    const firstColumn = activeBase.columns[0];

    return (
      <Typography className={classes.cardTitle}>
        <DragIndicatorIcon className={classes.dragIcon} fontSize="small" />
        {firstColumn?.type === "contact"
          ? renderContactValue(row.values?.[firstColumn.id])
          : getRowTitle(activeBase, row)}
      </Typography>
    );
  };

  const buildFileHref = url => {
    if (!url) return "";
    if (/^(https?:|blob:|data:)/i.test(url)) return url;
    return `${String(api.defaults.baseURL || "").replace(/\/+$/, "")}/${String(url).replace(/^\/+/, "")}`;
  };

  const getFilePreviewUrl = file => buildFileHref(file?.previewUrl || file?.url || "");

  const isImageFile = file =>
    String(file?.mimeType || file?.type || "").startsWith("image/") ||
    /^data:image\//.test(String(getFilePreviewUrl(file) || ""));

  const isVideoFile = file => String(file?.mimeType || file?.type || "").startsWith("video/");

  const isPdfFile = file =>
    String(file?.mimeType || file?.type || "").includes("pdf") ||
    String(file?.name || "").toLowerCase().endsWith(".pdf");

  const normalizeFileValue = value => {
    if (!value) return null;
    if (typeof value === "string") {
      return {
        name: "Archivo",
        type: value.startsWith("data:image/") ? "image" : "",
        previewUrl: value,
      };
    }
    return value;
  };

  const handleFileChange = async (row, column, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const { data } = await api.post("/workspace-data/files", formData);
      updateCell(row.id, column.id, {
        name: data.name || file.name,
        mimeType: data.mimeType || file.type,
        size: data.size || file.size,
        url: data.url,
      });
    } catch (err) {
      toastError(err);
    }
  };

  const renderFilesCell = (row, column) => {
    const file = normalizeFileValue(row.values?.[column.id]);
    const inputId = `workspace-file-${row.id}-${column.id}`;

    return (
      <div className={classes.fileCell}>
        <input
          id={inputId}
          type="file"
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.zip,.rar,.webp"
          hidden
          onChange={event => handleFileChange(row, column, event)}
        />
        {getFilePreviewUrl(file) ? (
          <div
            className={classes.filePreviewButton}
            role="button"
            tabIndex={0}
            onClick={() => setFilePreview(file)}
            onKeyDown={event => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setFilePreview(file);
              }
            }}
            onMouseDown={event => event.stopPropagation()}
          >
            {isImageFile(file) ? (
              <img
                src={getFilePreviewUrl(file)}
                alt={file.name || "Archivo"}
                className={classes.filePreviewThumb}
              />
            ) : (
              <span className={classes.filePreviewIcon}>
                <AttachFileIcon fontSize="small" />
              </span>
            )}
            <span className={classes.filePreviewName}>{file.name || "Archivo"}</span>
            <Tooltip title="Reemplazar archivo">
              <label
                htmlFor={inputId}
                className={classes.fileReplaceLabel}
                onClick={event => event.stopPropagation()}
              >
                <IconButton
                  className={classes.fileReplaceButton}
                  component="span"
                  size="small"
                  onMouseDown={event => event.stopPropagation()}
                >
                  <AttachFileIcon fontSize="small" />
                </IconButton>
              </label>
            </Tooltip>
          </div>
        ) : (
          <label htmlFor={inputId} className={classes.fileUploadLabel}>
            <Button
              className={classes.fileButton}
              component="span"
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
            >
              Añadir archivo o imagen
            </Button>
          </label>
        )}
      </div>
    );
  };

  const getActionHref = (type, value) => {
    const cleanValue = String(value || "").trim();
    if (!cleanValue) return "";
    if (type === "email") return `mailto:${cleanValue}`;
    if (type === "phone") return `tel:${cleanValue}`;
    if (type === "url") {
      return /^https?:\/\//i.test(cleanValue) ? cleanValue : `https://${cleanValue}`;
    }
    return "";
  };

  const getActionIcon = type => {
    if (type === "email") return <AlternateEmailIcon fontSize="small" />;
    if (type === "phone") return <PhoneIcon fontSize="small" />;
    return <LinkIcon fontSize="small" />;
  };

  const formatCurrencyValue = (column, value) => {
    if (value === "" || value === null || value === undefined) return "-";
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) return String(value);
    const decimals = Math.max(
      0,
      Number.isFinite(Number(column.currencyDecimals)) ? Number(column.currencyDecimals) : 2
    );
    return `${column.currencySymbol || "$"}${numberValue.toLocaleString("es-CR", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })}`;
  };

  const formatRowsCurrencyTotal = rows => {
    const currencyColumns = activeBase.columns.filter(column => column.type === "currency");
    if (!currencyColumns.length) return "";

    const total = rows.reduce((sum, row) => {
      const rowTotal = currencyColumns.reduce((columnSum, column) => {
        const value = Number(row.values?.[column.id]);
        return Number.isFinite(value) ? columnSum + value : columnSum;
      }, 0);
      return sum + rowTotal;
    }, 0);

    return formatCurrencyValue(currencyColumns[0], total);
  };

  const getFunnelById = funnelId =>
    funnels.find(funnel => String(funnel.id) === String(funnelId)) || null;

  const renderStageChip = stage => {
    if (!stage) return <span className={classes.emptySelectValue}>Sin etapa</span>;

    return (
      <Chip
        className={classes.optionPill}
        size="small"
        label={stage.name}
        style={{
          backgroundColor: stage.color || DEFAULT_OPTION_COLOR,
          color: getReadableTextColor(stage.color || DEFAULT_OPTION_COLOR),
        }}
      />
    );
  };

  const updateFunnelTicketStage = ({ funnelId, ticketId, stageId }) => {
    setFunnels(prevState =>
      prevState.map(funnel => {
        if (String(funnel.id) !== String(funnelId)) return funnel;

        const ticketStages = { ...(funnel.ticketStages || {}) };
        if (stageId) {
          ticketStages[String(ticketId)] = stageId;
        } else {
          delete ticketStages[String(ticketId)];
        }

        return { ...funnel, ticketStages };
      })
    );
  };

  const handleFunnelStageChange = async ({ row, column, stageId }) => {
    const funnelId = column.funnelId || funnels[0]?.id || "";
    const ticket = getTicketForRow(row);
    if (!funnelId || !ticket?.id) return;

    const previousFunnels = funnels;
    updateFunnelTicketStage({ funnelId, ticketId: ticket.id, stageId });

    try {
      await api.put(`/funnels/${funnelId}/tickets/${ticket.id}/move`, {
        stageId: stageId || null,
      });
    } catch (err) {
      setFunnels(previousFunnels);
      toastError(err);
    }
  };

  const renderFunnelStageCell = (row, column) => {
    const funnel = getFunnelById(column.funnelId || funnels[0]?.id || "");
    const ticket = getTicketForRow(row);
    const selectedStageId = ticket?.id
      ? funnel?.ticketStages?.[String(ticket.id)] || ""
      : "";
    const selectedStage = (funnel?.stages || []).find(
      stage => String(stage.id) === String(selectedStageId)
    );

    return (
      <FormControl
        variant="outlined"
        size="small"
        className={classes.selectCell}
        disabled={!funnel?.id || !ticket?.id}
      >
        <Select
          value={selectedStageId || NO_FUNNEL_STAGE}
          onOpen={fetchFunnels}
          onChange={event =>
            handleFunnelStageChange({
              row,
              column,
              stageId:
                event.target.value === NO_FUNNEL_STAGE ? "" : event.target.value,
            })
          }
          renderValue={() => renderStageChip(selectedStage)}
        >
          <MenuItem value={NO_FUNNEL_STAGE}>Sin etapa</MenuItem>
          {(funnel?.stages || []).map(stage => (
            <MenuItem key={stage.id} value={stage.id}>
              <span
                className={classes.optionDot}
                style={{ backgroundColor: stage.color || DEFAULT_OPTION_COLOR }}
              />
              {stage.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const getWorkspaceFilterValues = (row, column) => {
    const value = row.values?.[column.id];

    if (column.type === "multiSelect" || column.type === "relation") {
      return Array.isArray(value) ? value.map(item => String(item)).filter(Boolean) : [];
    }

    if (column.type === "ticketLabels") {
      const ticket = getTicketForRow(row);
      return (ticket?.labels || []).map(label => String(label.id)).filter(Boolean);
    }

    if (column.type === "funnelStage") {
      const funnel = getFunnelById(column.funnelId || funnels[0]?.id || "");
      const ticket = getTicketForRow(row);
      const stageId = ticket?.id ? funnel?.ticketStages?.[String(ticket.id)] || "" : "";
      return stageId ? [String(stageId)] : [];
    }

    if (column.type === "checkbox") {
      return [value ? WORKSPACE_FILTER_TRUE : WORKSPACE_FILTER_FALSE];
    }

    return value ? [String(value)] : [];
  };

  const getWorkspaceFilterLabel = (column, value) => {
    if (["select", "multiSelect", "status"].includes(column.type)) {
      return getOption(column, value)?.label || String(value);
    }

    if (column.type === "contact") {
      return getContactById(value)?.name || `Contacto ${value}`;
    }

    if (column.type === "person") {
      return getUserById(value)?.name || `Usuario ${value}`;
    }

    if (column.type === "ticketLabels") {
      const ticketLabel = activeRows
        .flatMap(row => getTicketForRow(row)?.labels || [])
        .find(item => String(item.id) === String(value));
      if (ticketLabel?.name) return ticketLabel.name;

      const label = Object.values(labelsByConnectionId)
        .flat()
        .find(item => String(item.id) === String(value));
      return label?.name || `Etiqueta ${value}`;
    }

    if (column.type === "funnelStage") {
      const funnel = getFunnelById(column.funnelId || funnels[0]?.id || "");
      const stage = (funnel?.stages || []).find(item => String(item.id) === String(value));
      return stage?.name || `Etapa ${value}`;
    }

    if (column.type === "checkbox") {
      return value === WORKSPACE_FILTER_TRUE ? "Marcado" : "Sin marcar";
    }

    if (column.type === "relation") {
      const relatedBase = state.bases.find(base => base.id === column.relationBaseId);
      const relatedRow = relatedBase?.rows?.find(row => String(row.id) === String(value));
      return relatedRow ? getRowTitle(relatedBase, relatedRow) : `Registro ${value}`;
    }

    return String(value);
  };

  const workspaceFilterOptions = useMemo(
    () =>
      filterableColumns
        .map(column => {
          const optionsById = new Map();

          activeRows.forEach(row => {
            getWorkspaceFilterValues(row, column).forEach(value => {
              if (!optionsById.has(value)) {
                optionsById.set(value, {
                  id: value,
                  name: getWorkspaceFilterLabel(column, value),
                });
              }
            });
          });

          return {
            column,
            options: Array.from(optionsById.values()).sort((first, second) =>
              String(first.name).localeCompare(String(second.name))
            ),
          };
        })
        .filter(item => item.options.length > 0),
    [
      activeRows,
      contactTicketsByContactId,
      contacts,
      filterableColumns,
      funnels,
      labelsByConnectionId,
      state.bases,
      users,
    ]
  );

  useEffect(() => {
    setRowFilters(prevState => {
      const availableByColumn = new Map(
        workspaceFilterOptions.map(({ column, options }) => [
          column.id,
          new Set(options.map(option => String(option.id))),
        ])
      );
      let changed = false;
      const nextFilters = {};

      Object.entries(prevState).forEach(([columnId, values]) => {
        const availableValues = availableByColumn.get(columnId);
        if (!availableValues) {
          changed = true;
          return;
        }

        const cleanValues = (values || []).filter(value =>
          availableValues.has(String(value))
        );
        if (cleanValues.length !== (values || []).length) changed = true;
        if (cleanValues.length) nextFilters[columnId] = cleanValues;
      });

      return changed ? nextFilters : prevState;
    });
  }, [workspaceFilterOptions]);

  const filteredRows = useMemo(
    () =>
      activeRows.filter(row =>
        filterableColumns.every(column => {
          const selectedValues = rowFilters[column.id] || [];
          if (!selectedValues.length) return true;

          const rowValues = getWorkspaceFilterValues(row, column);
          return selectedValues.some(value => rowValues.includes(String(value)));
        })
      ),
    [activeRows, filterableColumns, rowFilters, contactTicketsByContactId, funnels]
  );

  const activeFilterCount = Object.values(rowFilters).reduce(
    (count, values) => count + (values?.length ? 1 : 0),
    0
  );

  const renderWorkspaceFilterValue = (selected, options, fallback) => {
    if (!selected.length || selected.includes(WORKSPACE_FILTER_ALL)) return `Todos: ${fallback}`;
    return options
      .filter(option => selected.includes(String(option.id)))
      .map(option => option.name)
      .join(", ");
  };

  const renderWorkspaceFilters = () => {
    if (!workspaceFilterOptions.length) return null;

    return (
      <div className={classes.filtersBar}>
        {workspaceFilterOptions.map(({ column, options }) => {
          const selectedValues = rowFilters[column.id] || [];
          const selectValue = selectedValues.length ? selectedValues : [WORKSPACE_FILTER_ALL];

          return (
            <FormControl
              className={classes.filterControl}
              key={column.id}
              size="small"
              variant="outlined"
            >
              <InputLabel>{column.name}</InputLabel>
              <Select
                multiple
                value={selectValue}
                label={column.name}
                onChange={event => {
                  const nextValue = Array.isArray(event.target.value)
                    ? event.target.value
                    : String(event.target.value).split(",");
                  const nextFilters = nextValue.includes(WORKSPACE_FILTER_ALL)
                    ? !selectedValues.length && nextValue.length > 1
                      ? nextValue.filter(value => value !== WORKSPACE_FILTER_ALL)
                      : []
                    : nextValue;

                  setRowFilters(prevState => ({
                    ...prevState,
                    [column.id]: nextFilters,
                  }));
                }}
                renderValue={selected =>
                  renderWorkspaceFilterValue(selected, options, column.name)
                }
              >
                <MenuItem value={WORKSPACE_FILTER_ALL}>
                  <Checkbox checked={!selectedValues.length} />
                  <ListItemText primary="Todos" />
                </MenuItem>
                {options.map(option => (
                  <MenuItem key={option.id} value={option.id}>
                    <Checkbox checked={selectedValues.includes(option.id)} />
                    <ListItemText primary={option.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        })}
        {activeFilterCount > 0 && (
          <Button
            className={classes.clearFiltersButton}
            size="small"
            variant="outlined"
            onClick={() => setRowFilters({})}
          >
            Limpiar filtros
          </Button>
        )}
      </div>
    );
  };

  const renderCell = (row, column) => {
    const value = row.values?.[column.id] ?? "";

    if (["select", "status"].includes(column.type)) {
      return (
        <FormControl variant="outlined" size="small" className={classes.selectCell}>
          <Select
            value={value || NO_SELECT_VALUE}
            onChange={event =>
              updateCell(
                row.id,
                column.id,
                event.target.value === NO_SELECT_VALUE ? "" : event.target.value
              )
            }
            renderValue={selected => renderOptionChip(column, selected)}
          >
            <MenuItem value={NO_SELECT_VALUE}>Sin selección</MenuItem>
            {(column.options || []).map(option => (
              <MenuItem key={option.id} value={option.id}>
                <span className={classes.optionDot} style={{ backgroundColor: option.color }} />
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (column.type === "multiSelect") {
      const selectedValues = Array.isArray(value) ? value : [];
      return (
        <FormControl variant="outlined" size="small" className={classes.selectCell}>
          <Select
            multiple
            displayEmpty
            value={selectedValues}
            onChange={event => updateCell(row.id, column.id, event.target.value)}
            renderValue={selected =>
              renderLimitedChips(selected, optionId => renderOptionChip(column, optionId), 2, "Seleccionar")
            }
          >
            {(column.options || []).map(option => (
              <MenuItem key={option.id} value={option.id}>
                <Checkbox checked={selectedValues.includes(option.id)} />
                <span className={classes.optionDot} style={{ backgroundColor: option.color }} />
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (column.type === "relation") {
      const selectedValues = Array.isArray(value) ? value : [];
      const relatedBase = state.bases.find(base => base.id === column.relationBaseId);
      const relationIsMultiple = column.relationLimit !== "one";

      return (
        <FormControl variant="outlined" size="small" className={classes.selectCell}>
          <Select
            multiple={relationIsMultiple}
            displayEmpty
            value={relationIsMultiple ? selectedValues : selectedValues[0] || ""}
            onChange={event => {
              const nextValue = relationIsMultiple
                ? event.target.value
                : event.target.value
                ? [event.target.value]
                : [];
              updateCell(row.id, column.id, nextValue);
            }}
            renderValue={selected =>
              renderRelationValue(column, Array.isArray(selected) ? selected : [selected].filter(Boolean))
            }
          >
            {(relatedBase?.rows || []).map(relatedRow => (
              <MenuItem key={relatedRow.id} value={relatedRow.id}>
                {relationIsMultiple && (
                  <Checkbox checked={selectedValues.includes(relatedRow.id)} />
                )}
                {getRowTitle(relatedBase, relatedRow)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (column.type === "person") {
      const selectedUser = getUserById(value);

      return (
        <Autocomplete
          className={classes.contactCell}
          size="small"
          options={users}
          value={selectedUser}
          getOptionLabel={option => option?.name || ""}
          getOptionSelected={(option, selected) =>
            String(option?.id) === String(selected?.id)
          }
          onOpen={() => fetchUsers()}
          onInputChange={(_event, nextValue, reason) => {
            if (reason === "input") {
              scheduleUserSearch(nextValue);
            }
          }}
          onChange={(_event, user) => {
            updateCell(row.id, column.id, user?.id || "");
            if (user?.id) fetchUserById(user.id);
          }}
          renderOption={renderUserOption}
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              placeholder="Seleccionar usuario"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    {selectedUser && (
                      <Avatar
                        src={selectedUser.profilePicUrl}
                        className={classes.contactAvatar}
                      >
                        {String(selectedUser.name || "?").slice(0, 1).toUpperCase()}
                      </Avatar>
                    )}
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      );
    }

    if (column.type === "contact") {
      const selectedContact = getContactById(value);

      return (
        <Autocomplete
          className={classes.contactCell}
          size="small"
          options={contacts}
          value={selectedContact}
          getOptionLabel={option => option?.name || ""}
          getOptionSelected={(option, selected) =>
            String(option?.id) === String(selected?.id)
          }
          onOpen={() => fetchContacts()}
          onInputChange={(_event, nextValue, reason) => {
            if (reason === "input") {
              scheduleContactSearch(nextValue);
            }
          }}
          onChange={(_event, contact) => {
            updateCell(row.id, column.id, contact?.id || "");
            if (contact?.id) {
              fetchContactById(contact.id);
              fetchContactTicket(contact.id);
            }
          }}
          renderOption={renderContactOption}
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              placeholder="Seleccionar contacto"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    {selectedContact && (
                      <Avatar
                        src={selectedContact.profilePicUrl}
                        className={classes.contactAvatar}
                      />
                    )}
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      );
    }

    if (column.type === "ticketLabels") {
      return renderTicketLabelsCell(row);
    }

    if (column.type === "contactData") {
      return renderContactDataCell(row, column);
    }

    if (column.type === "funnelStage") {
      return renderFunnelStageCell(row, column);
    }

    if (column.type === "contactAction") {
      return renderContactActionCell(row, column);
    }

    if (column.type === "files") {
      return renderFilesCell(row, column);
    }

    if (column.type === "checkbox") {
      return (
        <span className={classes.checkboxCell}>
          <Checkbox
            color="primary"
            checked={Boolean(value)}
            onChange={event => updateCell(row.id, column.id, event.target.checked)}
          />
        </span>
      );
    }

    if (column.type === "longText") {
      return (
        <TextField
          className={classes.cellInput}
          value={value}
          onChange={event => updateCell(row.id, column.id, event.target.value)}
          variant="outlined"
          size="small"
          multiline
          minRows={2}
        />
      );
    }

    if (column.type === "currency") {
      return (
        <TextField
          className={classes.cellInput}
          type="number"
          value={value}
          onChange={event => updateCell(row.id, column.id, event.target.value)}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {column.currencySymbol || "$"}
              </InputAdornment>
            ),
          }}
        />
      );
    }

    const actionHref = ["url", "phone", "email"].includes(column.type)
      ? getActionHref(column.type, value)
      : "";

    return (
      <TextField
        className={`${classes.cellInput} ${
          ["url", "phone", "email"].includes(column.type) ? classes.linkedInput : ""
        }`}
        type={
          column.type === "number"
            ? "number"
            : column.type === "date"
            ? "date"
            : column.type === "email"
            ? "email"
            : column.type === "url"
            ? "url"
            : column.type === "phone"
            ? "tel"
            : "text"
        }
        value={value}
        onChange={event => updateCell(row.id, column.id, event.target.value)}
        variant="outlined"
        size="small"
        InputProps={{
          endAdornment: ["url", "phone", "email"].includes(column.type) ? (
            <InputAdornment position="end">
              <Tooltip title={actionHref ? "Abrir" : "Sin valor"}>
                <span>
                  <IconButton
                    edge="end"
                    size="small"
                    disabled={!actionHref}
                    onClick={() => {
                      if (!actionHref) return;
                      if (column.type === "url") {
                        window.open(actionHref, "_blank", "noopener,noreferrer");
                      } else {
                        window.location.href = actionHref;
                      }
                    }}
                  >
                    {getActionIcon(column.type)}
                  </IconButton>
                </span>
              </Tooltip>
            </InputAdornment>
          ) : null,
        }}
      />
    );
  };

  const renderRowEditorDialog = () => (
    <Dialog
      open={Boolean(rowEditorRow)}
      onClose={() => setRowEditorId("")}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        {rowEditorRow && activeBase.columns[0]?.type === "contact"
          ? renderContactValue(rowEditorRow.values?.[activeBase.columns[0].id])
          : rowEditorRow
          ? getRowTitle(activeBase, rowEditorRow)
          : "Registro"}
      </DialogTitle>
      <DialogContent dividers className={classes.rowEditorContent}>
        {rowEditorRow &&
          activeBase.columns.map(column => (
            <div className={classes.rowEditorField} key={column.id}>
              <span className={classes.rowEditorLabel}>
                {getType(column.type).icon}
                {column.name}
              </span>
              {renderCell(rowEditorRow, column)}
            </div>
          ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setRowEditorId("")} color="primary">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderTableView = () => (
    <div className={classes.tableWrap}>
      <Table size="small" className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={`${classes.tableHeadCell} ${classes.dragCell}`} />
            {activeBase.columns.map(column => (
              <TableCell
                key={column.id}
                className={`${classes.tableHeadCell} ${classes.columnHeaderCell} ${
                  column.id === activeBase.columns[0]?.id ? classes.rowTitleCell : ""
                } ${propertyColumnDropId === String(column.id) && propertyColumnDropPosition === "before"
                  ? classes.tableColumnDropBefore
                  : ""} ${propertyColumnDropId === String(column.id) && propertyColumnDropPosition === "after"
                  ? classes.tableColumnDropAfter
                  : ""}`}
                onDragOver={event => handlePropertyColumnDragOver(column.id)(event)}
                onDrop={event => handlePropertyColumnDrop(column.id)(event)}
              >
                <span className={classes.tableHeaderContent}>
                  <span className={classes.hoverActions}>
                    <Tooltip title="Mover columna">
                      <IconButton
                        className={classes.rowDragHandle}
                        size="small"
                        draggable
                        onDragStart={handlePropertyColumnDragStart(column.id)}
                        onDragEnd={handlePropertyColumnDragEnd}
                      >
                        <DragIndicatorIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </span>
                  {getType(column.type).icon}
                  {column.name}
                  <span className={classes.hoverActions}>
                    <Tooltip title="Editar propiedad">
                      <IconButton size="small" onClick={() => openEditProperty(column)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Insertar propiedad">
                      <IconButton size="small" onClick={event => openInsertMenu(event, column.id)}>
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {activeBase.columns.length > 1 && (
                      <Tooltip title="Eliminar propiedad">
                        <IconButton size="small" onClick={() => requestDeleteColumn(column.id)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </span>
                </span>
              </TableCell>
            ))}
            <TableCell className={classes.tableHeadCell} />
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredRows.map(row => (
            <TableRow
              key={row.id}
              hover
              draggable
              className={`${classes.tableRow} ${rowDropTarget?.rowId === row.id && rowDropTarget?.placement === "before"
                ? classes.tableRowDropBefore
                : ""} ${rowDropTarget?.rowId === row.id && rowDropTarget?.placement === "after"
                ? classes.tableRowDropAfter
                : ""}`}
              onDragStart={event => {
                event.dataTransfer.setData(ROW_DRAG_TYPE, row.id);
                event.dataTransfer.setData("text/plain", row.id);
                event.dataTransfer.effectAllowed = "move";
                setDraggingRowId(String(row.id));
              }}
              onDragEnd={() => {
                setDraggingRowId("");
                setRowDropTarget(null);
              }}
              onDragOver={event => {
                if (!draggingRowId || draggingRowId === row.id) return;
                event.preventDefault();
                const bounds = event.currentTarget.getBoundingClientRect();
                const placement = event.clientY > bounds.top + bounds.height / 2 ? "after" : "before";
                const nextTarget = { rowId: String(row.id), placement };
                setRowDropTarget(prevState =>
                  prevState?.rowId === nextTarget.rowId &&
                  prevState?.placement === nextTarget.placement
                    ? prevState
                    : nextTarget
                );
              }}
              onDrop={event => {
                const draggedRowId = event.dataTransfer.getData(ROW_DRAG_TYPE);
                if (!draggedRowId) return;
                event.preventDefault();
                reorderRows({
                  rowId: draggedRowId,
                  targetRowId: row.id,
                  placement: rowDropTarget?.placement || "after",
                });
              }}
            >
              <TableCell className={classes.dragCell}>
                <span className={classes.rowHoverActions}>
                  <Tooltip title="Agregar registro debajo">
                    <IconButton size="small" onClick={() => addRowBelow(row.id)}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Mover fila">
                    <IconButton className={classes.rowDragHandle} size="small">
                      <DragIndicatorIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </span>
              </TableCell>
              {activeBase.columns.map(column => (
                <TableCell key={column.id}>{renderCell(row, column)}</TableCell>
              ))}
              <TableCell className={classes.rowActions}>
                <span className={classes.rowHoverActions}>
                  <IconButton size="small" onClick={() => requestDeleteRow(row.id)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {!filteredRows.length && (
        <div className={classes.emptyState}>
          {activeRows.length
            ? "No hay registros que coincidan con los filtros."
            : "Agrega un registro para empezar a organizar datos."}
        </div>
      )}
    </div>
  );

  const renderKanbanCard = (row, groupId) => (
    <Paper
      key={row.id}
      className={`${classes.card} ${
        rowDropTarget?.rowId === row.id && rowDropTarget?.placement === "before"
          ? classes.cardDropBefore
          : ""
      } ${
        rowDropTarget?.rowId === row.id && rowDropTarget?.placement === "after"
          ? classes.cardDropAfter
          : ""
      }`}
      draggable
      elevation={0}
      onDragStart={event => {
        event.dataTransfer.setData(ROW_DRAG_TYPE, row.id);
        event.dataTransfer.setData("text/plain", row.id);
        event.dataTransfer.effectAllowed = "move";
        setDraggingRowId(String(row.id));
      }}
      onDragEnd={() => {
        setDraggingRowId("");
        setRowDropTarget(null);
      }}
      onDragOver={event => {
        if (!draggingRowId) return;
        event.preventDefault();
        event.stopPropagation();
        const bounds = event.currentTarget.getBoundingClientRect();
        const placement = event.clientY > bounds.top + bounds.height / 2 ? "after" : "before";
        const nextTarget = { groupId: String(groupId), rowId: String(row.id), placement };
        setRowDropTarget(prevState =>
          prevState?.groupId === nextTarget.groupId &&
          prevState?.rowId === nextTarget.rowId &&
          prevState?.placement === nextTarget.placement
            ? prevState
            : nextTarget
        );
      }}
      onDrop={event => {
        const draggedRowId = event.dataTransfer.getData(ROW_DRAG_TYPE);
        if (!draggedRowId) return;
        event.preventDefault();
        event.stopPropagation();
        moveRowToGroup({
          rowId: draggedRowId,
          groupId,
          targetRowId: row.id,
          placement: rowDropTarget?.placement || "after",
        });
      }}
      onDoubleClick={() => setRowEditorId(row.id)}
    >
      {renderKanbanCardTitle(row)}
      <div className={classes.cardMeta}>
        {activeBase.columns
          .filter(column => column.id !== activeBase.columns[0]?.id && column.id !== groupColumn.id)
          .slice(0, 4)
          .map(column => (
            <div className={classes.metaLine} key={column.id}>
              {getType(column.type).icon}
              <span>{column.name}:</span>
              {["select", "status"].includes(column.type) ? (
                renderOptionChip(column, row.values?.[column.id])
              ) : column.type === "multiSelect" ? (
                renderLimitedChips(
                  row.values?.[column.id] || [],
                  optionId => renderOptionChip(column, optionId),
                  2,
                  "-"
                )
              ) : column.type === "relation" ? (
                renderRelationValue(column, row.values?.[column.id] || [])
              ) : column.type === "contact" ? (
                renderContactValue(row.values?.[column.id])
              ) : column.type === "person" ? (
                renderUserValue(row.values?.[column.id])
              ) : column.type === "ticketLabels" ? (
                renderTicketLabelsValue(
                  (getTicketForRow(row)?.labels || []).map(label => label.id),
                  getLabelsForTicket(getTicketForRow(row))
                )
              ) : column.type === "contactData" ? (
                <strong>
                  {getContactDataValue(getContactById(getRowContactId(row)), column) || "-"}
                </strong>
              ) : column.type === "funnelStage" ? (
                renderStageChip(
                  (getFunnelById(column.funnelId || funnels[0]?.id || "")?.stages || []).find(
                    stage =>
                      String(stage.id) ===
                      String(
                        getFunnelById(column.funnelId || funnels[0]?.id || "")
                          ?.ticketStages?.[String(getTicketForRow(row)?.id || "")]
                      )
                  )
                )
              ) : column.type === "contactAction" ? (
                renderContactActionCell(row, column)
              ) : column.type === "files" ? (
                renderFilesCell(row, column)
              ) : column.type === "currency" ? (
                <strong>{formatCurrencyValue(column, row.values?.[column.id])}</strong>
              ) : column.type === "longText" ? (
                <strong>{String(row.values?.[column.id] || "-")}</strong>
              ) : (
                <strong>{String(row.values?.[column.id] || "-")}</strong>
              )}
            </div>
          ))}
      </div>
    </Paper>
  );

  const renderKanbanView = () => {
    if (!groupColumn || !["select", "status"].includes(groupColumn.type)) {
      return (
        <div className={classes.emptyState}>
          Selecciona una propiedad de tipo seleccionar o estado para agrupar el tablero.
        </div>
      );
    }

    const groups = [
      { id: "__empty__", label: "Sin selección", color: "#E5E7EB" },
      ...(groupColumn.options || []),
    ];
    const getRowGroupValue = row =>
      row.values?.[groupColumn.id] ? String(row.values[groupColumn.id]) : "__empty__";

    return (
      <div className={classes.kanban}>
        {groups.map(group => {
          const groupRows = filteredRows.filter(row => getRowGroupValue(row) === String(group.id));
          const groupCurrencyTotal = formatRowsCurrencyTotal(groupRows);

          return (
            <section
              className={`${classes.kanbanColumn} ${
                columnDropId === String(group.id) ? classes.columnDraggingOver : ""
              } ${
                columnDropId === String(group.id) && columnDropPosition === "before"
                  ? classes.columnDropBefore
                  : ""
              } ${
                columnDropId === String(group.id) && columnDropPosition === "after"
                  ? classes.columnDropAfter
                  : ""
              }`}
              key={group.id}
              onDragOver={handleColumnDragOver(group.id)}
              onDrop={handleColumnDrop(group)}
            >
              <div className={classes.kanbanTitle}>
                <Tooltip title="Mover columna">
                  <IconButton
                    size="small"
                    draggable
                    onDragStart={handleColumnDragStart(group.id)}
                    onDragEnd={handleColumnDragEnd}
                  >
                    <DragIndicatorIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Chip
                  className={classes.optionPill}
                  size="small"
                  label={group.label}
                  style={{ backgroundColor: group.color, color: "#243040" }}
                />
                <span className={classes.kanbanCount}>{groupRows.length}</span>
                {groupCurrencyTotal && (
                  <Chip
                    className={classes.kanbanTotal}
                    size="small"
                    label={`Total ${groupCurrencyTotal}`}
                  />
                )}
              </div>
              {groupRows.length ? (
                groupRows.map(row => renderKanbanCard(row, group.id))
              ) : (
                <div className={classes.emptyState}>Arrastra registros aquí.</div>
              )}
            </section>
          );
        })}
      </div>
    );
  };

  const getRowsForCalendarDay = day => {
    if (!calendarColumn) return [];
    const dayValue = toDateInput(day);
    return filteredRows.filter(row => String(row.values?.[calendarColumn.id] || "") === dayValue);
  };

  const moveCalendarMonth = amount => {
    setCalendarDate(prevState => {
      const next = new Date(prevState);
      next.setMonth(next.getMonth() + amount);
      return next;
    });
  };

  const renderCalendarCard = row => {
    const firstColumn = activeBase.columns[0];
    const currencyColumn = activeBase.columns.find(column => column.type === "currency");
    const currencyValue = currencyColumn
      ? formatCurrencyValue(currencyColumn, row.values?.[currencyColumn.id])
      : "";

    return (
      <div
        className={classes.calendarCard}
        key={row.id}
        onDoubleClick={() => setRowEditorId(row.id)}
      >
        <div className={classes.calendarCardTitle}>
          {firstColumn?.type === "contact"
            ? renderContactValue(row.values?.[firstColumn.id])
            : getRowTitle(activeBase, row)}
        </div>
        {currencyColumn && currencyValue !== "-" && (
          <div className={classes.calendarCardMeta}>{currencyValue}</div>
        )}
      </div>
    );
  };

  const renderCalendarView = () => {
    if (!calendarColumns.length) {
      return (
        <div className={classes.emptyState}>
          Crea una propiedad de tipo fecha para usar la vista calendario.
        </div>
      );
    }

    return (
      <>
        <div className={classes.calendarToolbar}>
          <Button size="small" variant="outlined" onClick={() => moveCalendarMonth(-1)}>
            Anterior
          </Button>
          <TextField
            className={classes.calendarDateControl}
            label="Mes"
            type="month"
            value={`${calendarDate.getFullYear()}-${padDatePart(calendarDate.getMonth() + 1)}`}
            onChange={event => {
              const [year, month] = String(event.target.value || "").split("-").map(Number);
              if (!year || !month) return;
              setCalendarDate(new Date(year, month - 1, 1));
            }}
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <Button size="small" variant="outlined" onClick={() => moveCalendarMonth(1)}>
            Siguiente
          </Button>
        </div>
        <div className={classes.calendar}>
          {calendarDays.map(day => {
            const dayRows = getRowsForCalendarDay(day);
            const isCurrentMonth = day.getMonth() === calendarDate.getMonth();

            return (
              <Paper
                key={day.toISOString()}
                className={`${classes.calendarDay} ${!isCurrentMonth ? classes.calendarDayMuted : ""}`}
                elevation={0}
              >
                <div className={classes.calendarDayHeader}>
                  <Typography variant="subtitle2" color={isCurrentMonth ? "textPrimary" : "textSecondary"}>
                    {`${weekdays[day.getDay()]} ${day.getDate()}`}
                  </Typography>
                  <IconButton size="small" onClick={() => addCalendarRow(day)}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </div>
                {dayRows.map(renderCalendarCard)}
              </Paper>
            );
          })}
        </div>
      </>
    );
  };

  const selectableBases = state.bases.filter(base => base.id !== activeBase.id);
  const showOptionsEditor = ["select", "multiSelect", "status"].includes(newProperty.type);

  if (loading) {
    return (
      <MainContainer>
        <div className={classes.page}>
          <div className={classes.emptyState}>
            <CircularProgress size={24} />
            <Typography variant="body2" style={{ marginLeft: 12 }}>
              Cargando datos...
            </Typography>
          </div>
        </div>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <div className={classes.page}>
        <ConfirmationModal
          title="Eliminar fila"
          open={Boolean(deletingRow)}
          onClose={() => setDeletingRow(null)}
          onConfirm={confirmDeleteRow}
        >
          Esta fila se eliminará de la base actual. Esta acción no se puede deshacer.
        </ConfirmationModal>
        <ConfirmationModal
          title="Eliminar columna"
          open={Boolean(deletingColumn)}
          onClose={() => setDeletingColumn(null)}
          onConfirm={confirmDeleteColumn}
        >
          Esta columna y sus datos se eliminarán de la base actual. Esta acción no se puede deshacer.
        </ConfirmationModal>
        <ConfirmationModal
          title="Eliminar base"
          open={Boolean(deletingBase)}
          onClose={() => setDeletingBase(null)}
          onConfirm={confirmDeleteBase}
        >
          Esta base, sus columnas y sus filas se eliminaran. Esta accion no se puede deshacer.
        </ConfirmationModal>
        <TicketRemindersModal
          modalOpen={contactActionModal.action === "reminders"}
          onClose={closeContactActionModal}
          ticket={contactActionModal.ticket}
        />
        <TicketAgendaModal
          modalOpen={contactActionModal.action === "agenda"}
          onClose={closeContactActionModal}
          ticket={contactActionModal.ticket}
        />
        <TicketOrdersModal
          modalOpen={contactActionModal.action === "orders"}
          onClose={closeContactActionModal}
          ticket={contactActionModal.ticket}
        />
        <TicketProcessesModal
          modalOpen={contactActionModal.action === "processes"}
          onClose={closeContactActionModal}
          ticket={contactActionModal.ticket}
        />
        <TicketStreamingModal
          modalOpen={contactActionModal.action === "streaming"}
          onClose={closeContactActionModal}
          ticket={contactActionModal.ticket}
        />
        <Dialog
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Google Sheets</DialogTitle>
          <DialogContent dividers>
            <FormControl fullWidth margin="dense" variant="outlined" disabled={sheetLoading}>
              <InputLabel>Base</InputLabel>
              <Select
                label="Base"
                value={sheetBaseId || NO_WORKSPACE_BASE}
                onChange={event => {
                  const nextBaseId = event.target.value;
                  setSheetBaseId(nextBaseId);
                  loadSheetConfig(nextBaseId);
                }}
              >
                <MenuItem value={NO_WORKSPACE_BASE}>Seleccionar base</MenuItem>
                {state.bases.map(base => (
                  <MenuItem key={base.id} value={base.id}>
                    {base.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <div className={classes.sheetConfigFields}>
              <TextField
                fullWidth
                margin="dense"
                variant="outlined"
                label="ID del documento"
                value={sheetSpreadsheetId}
                disabled={sheetLoading}
                onChange={event => setSheetSpreadsheetId(event.target.value)}
              />
              <TextField
                fullWidth
                margin="dense"
                variant="outlined"
                label="Hoja"
                value={sheetName}
                disabled={sheetLoading}
                onChange={event => setSheetName(event.target.value)}
              />
            </div>
            <Typography
              variant="body2"
              color="textSecondary"
              className={classes.sheetConfigStatus}
            >
              {sheetStatus}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSheetOpen(false)}>Cerrar</Button>
            <Button
              color="primary"
              variant="outlined"
              disabled={sheetLoading || sheetSaving || sheetSyncing}
              onClick={() => saveSheetConfig(true)}
            >
              {sheetSaving ? "Guardando..." : "Guardar"}
            </Button>
            <Button
              color="primary"
              variant="contained"
              disabled={sheetLoading || sheetSaving || sheetSyncing}
              onClick={syncWorkspaceBaseToSheet}
            >
              {sheetSyncing ? "Enviando..." : "Guardar y enviar"}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={Boolean(filePreview)}
          onClose={() => setFilePreview(null)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>{filePreview?.name || "Archivo"}</DialogTitle>
          <DialogContent dividers>
            {isImageFile(filePreview) ? (
              <img
                src={getFilePreviewUrl(filePreview)}
                alt={filePreview?.name || "Archivo"}
                className={classes.filePreviewImage}
              />
            ) : isVideoFile(filePreview) ? (
              <video
                src={getFilePreviewUrl(filePreview)}
                className={classes.filePreviewVideo}
                controls
              />
            ) : isPdfFile(filePreview) ? (
              <iframe
                title={filePreview?.name || "Archivo PDF"}
                src={getFilePreviewUrl(filePreview)}
                className={classes.filePreviewFrame}
              />
            ) : (
              <div className={classes.emptyState}>Vista previa no disponible.</div>
            )}
          </DialogContent>
          <DialogActions>
            {getFilePreviewUrl(filePreview) && (
              <Button
                component="a"
                href={getFilePreviewUrl(filePreview)}
                download={filePreview.name || "archivo"}
                color="primary"
              >
                Descargar
              </Button>
            )}
            <Button onClick={() => setFilePreview(null)} color="secondary">
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
        {renderRowEditorDialog()}
        <header className={classes.header}>
          <div>
            <div className={classes.eyebrow}>Vistas inteligentes</div>
            <Typography component="h1" className={classes.title}>
              Organizado a tu manera
            </Typography>
            <Typography className={classes.subtitle}>
              Segmenta, relaciona y edita la información de tus contactos con vistas adaptadas a tu operación.
            </Typography>
          </div>
          <div className={classes.actions}>
            <Button variant="outlined" color="primary" startIcon={<AddIcon />} onClick={openNewBaseDialog}>
              Nueva base
            </Button>
            <Button variant="outlined" color="primary" startIcon={<ViewColumnIcon />} onClick={() => openNewPropertyDialog(null)}>
              Nueva propiedad
            </Button>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={addRow}>
              Nuevo registro
            </Button>
            <Tooltip title="Configurar Google Sheets">
              <span>
                <IconButton
                  color="primary"
                  size="small"
                  onClick={openSheetModal}
                  disabled={!state.bases.length}
                  aria-label="Configurar Google Sheets"
                >
                  <SettingsOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </header>

        <Paper className={classes.workspace}>
          <div className={classes.toolbar}>
            <div className={classes.toolbarLeft}>
              <div className={classes.baseSelectorGroup}>
                <FormControl variant="outlined" size="small" className={classes.baseControl}>
                  <InputLabel>Base</InputLabel>
                  <Select
                    value={activeBase.id}
                    onChange={event =>
                      setState(prevState => ({ ...prevState, activeBaseId: event.target.value }))
                    }
                    label="Base"
                  >
                    {state.bases.map(base => (
                      <MenuItem key={base.id} value={base.id}>
                        {base.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip title="Editar base">
                  <span>
                    <IconButton
                      className={classes.baseActionButton}
                      size="small"
                      disabled={!activeBase?.id}
                      onClick={openEditBaseDialog}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Eliminar base actual">
                  <span>
                    <IconButton
                      className={classes.baseActionButton}
                      size="small"
                      disabled={state.bases.length <= 1}
                      onClick={requestDeleteBase}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </div>
            <Tabs
              value={view}
              onChange={(_event, nextView) => setView(nextView)}
              indicatorColor="primary"
              textColor="primary"
              className={classes.tabs}
            >
              <Tab value="table" icon={<ViewColumnIcon />} label="Tabla" />
              <Tab value="kanban" icon={<ViewColumnIcon />} label="Kanban" />
              <Tab value="calendar" icon={<EventOutlinedIcon />} label="Calendario" />
            </Tabs>
            <div className={classes.toolbarRight}>
              {view === "kanban" && (
                <FormControl variant="outlined" size="small" className={classes.groupingControl}>
                  <InputLabel>Agrupar por</InputLabel>
                  <Select
                    value={activeBase.groupBy || NO_GROUP_COLUMN}
                    onChange={event =>
                      updateActiveBase(base => ({
                        ...base,
                        groupBy: event.target.value === NO_GROUP_COLUMN ? "" : event.target.value,
                      }))
                    }
                    label="Agrupar por"
                  >
                    <MenuItem value={NO_GROUP_COLUMN}>Sin agrupación</MenuItem>
                    {groupedColumns.map(column => (
                      <MenuItem key={column.id} value={column.id}>
                        {column.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {view === "calendar" && calendarColumns.length > 0 && (
                <FormControl variant="outlined" size="small" className={classes.groupingControl}>
                  <InputLabel>Fecha</InputLabel>
                  <Select
                    value={calendarColumn?.id || NO_CALENDAR_DATE_COLUMN}
                    onChange={event => setCalendarColumnId(event.target.value)}
                    label="Fecha"
                  >
                    {calendarColumns.map(column => (
                      <MenuItem key={column.id} value={column.id}>
                        {column.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </div>
          </div>
          {renderWorkspaceFilters()}
          {view === "table"
            ? renderTableView()
            : view === "kanban"
            ? renderKanbanView()
            : renderCalendarView()}
        </Paper>

        <Menu
          anchorEl={insertMenuAnchor}
          open={Boolean(insertMenuAnchor)}
          onClose={closeInsertMenu}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <MenuItem onClick={() => insertColumnNear("left")}>
            <ListItemText primary="Insertar a la izquierda" />
          </MenuItem>
          <MenuItem onClick={() => insertColumnNear("right")}>
            <ListItemText primary="Insertar a la derecha" />
          </MenuItem>
        </Menu>

        <Dialog open={baseDialogOpen} onClose={closeBaseDialog} fullWidth maxWidth="xs">
          <form onSubmit={saveBase}>
            <DialogTitle>{editingBaseId ? "Editar base" : "Nueva base"}</DialogTitle>
            <DialogContent dividers>
              <TextField
                autoFocus
                fullWidth
                label="Nombre"
                value={newBaseName}
                onChange={event => setNewBaseName(event.target.value)}
                variant="outlined"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={closeBaseDialog} color="secondary">
                Cancelar
              </Button>
              <Button type="submit" color="primary" variant="contained" disabled={!newBaseName.trim()}>
                {editingBaseId ? "Guardar" : "Crear base"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        <Dialog open={editPropertyDialogOpen} onClose={closeEditProperty} fullWidth maxWidth="sm">
          <DialogTitle>Editar propiedad</DialogTitle>
          <DialogContent dividers>
            {editingProperty && (
              <div className={classes.dialogGrid}>
                <TextField
                  label="Nombre"
                  value={editingProperty.name}
                  onChange={event =>
                    setEditingProperty(prevState => ({ ...prevState, name: event.target.value }))
                  }
                  variant="outlined"
                  fullWidth
                  autoFocus
                />
                <FormControl variant="outlined" fullWidth>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={editingProperty.type}
                    renderValue={renderTypeValue}
                    onChange={event => {
                      const nextType = event.target.value;
                      setEditingProperty(prevState => ({
                        ...prevState,
                        type: nextType,
                        options: ["select", "multiSelect", "status"].includes(nextType)
                          ? prevState.options?.length
                            ? prevState.options
                            : [
                                { id: makeId("option"), label: "Nuevo", color: "#BAE6FD" },
                                { id: makeId("option"), label: "En proceso", color: "#FEF3C7" },
                                { id: makeId("option"), label: "Cerrado", color: "#BBF7D0" },
                              ]
                          : [],
                        relationBaseId:
                          nextType === "relation"
                            ? prevState.relationBaseId ||
                              state.bases.find(base => base.id !== activeBase.id)?.id ||
                              state.bases[0]?.id ||
                              ""
                            : prevState.relationBaseId,
                      }));
                    }}
                    label="Tipo"
                  >
                    {availablePropertyTypes.map(type => (
                      <MenuItem key={type.value} value={type.value}>
                        <ListItemIcon>{type.icon}</ListItemIcon>
                        <ListItemText primary={type.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {editingProperty.type === "relation" && (
                  <div className={classes.relationSettings}>
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel>Relacionada con</InputLabel>
                      <Select
                        value={editingProperty.relationBaseId || state.bases[0]?.id || ""}
                        onChange={event =>
                          setEditingProperty(prevState => ({
                            ...prevState,
                            relationBaseId: event.target.value,
                          }))
                        }
                        label="Relacionada con"
                      >
                        {state.bases.map(base => (
                          <MenuItem key={base.id} value={base.id}>
                            {base.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel>Límite</InputLabel>
                      <Select
                        value={editingProperty.relationLimit || "none"}
                        onChange={event =>
                          setEditingProperty(prevState => ({
                            ...prevState,
                            relationLimit: event.target.value,
                          }))
                        }
                        label="Límite"
                      >
                        <MenuItem value="none">Sin límite</MenuItem>
                        <MenuItem value="one">Un registro</MenuItem>
                      </Select>
                    </FormControl>
                    <MenuItem
                      onClick={() =>
                        setEditingProperty(prevState => ({
                          ...prevState,
                          bidirectional: !prevState.bidirectional,
                        }))
                      }
                    >
                      <Checkbox checked={Boolean(editingProperty.bidirectional)} />
                      <ListItemText
                        primary="Relación bidireccional"
                        secondary={editingProperty.bidirectional ? "Activado" : "Desactivado"}
                      />
                    </MenuItem>
                  </div>
                )}
                {editingProperty.type === "contactData" &&
                  renderContactDataSettings(editingProperty, setEditingProperty)}
                {editingProperty.type === "funnelStage" &&
                  renderFunnelStageSettings(editingProperty, setEditingProperty)}
                {editingProperty.type === "contactAction" &&
                  renderContactActionSettings(editingProperty, setEditingProperty)}
                {editingProperty.type === "currency" &&
                  renderCurrencySettings(editingProperty, setEditingProperty)}
                {["select", "multiSelect", "status"].includes(editingProperty.type) && (
                  <div className={classes.optionEditor}>
                    <Typography variant="subtitle2">Opciones y colores</Typography>
                    {editingProperty.options.map(option => (
                      <div className={classes.optionRow} key={option.id}>
                        <Button
                          className={classes.colorButton}
                          style={{ backgroundColor: option.color }}
                          onClick={() => {
                            const index = optionPalette.indexOf(option.color);
                            updateEditingOption(option.id, {
                              color: optionPalette[(index + 1) % optionPalette.length],
                            });
                          }}
                        />
                        <TextField
                          value={option.label}
                          onChange={event =>
                            updateEditingOption(option.id, { label: event.target.value })
                          }
                          variant="outlined"
                          size="small"
                        />
                        <IconButton size="small" onClick={() => removeEditingOption(option.id)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </div>
                    ))}
                    <Button color="primary" startIcon={<AddIcon />} onClick={addEditingOption}>
                      Agregar opción
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeEditProperty} color="secondary">
              Cancelar
            </Button>
            <Button
              onClick={saveEditProperty}
              color="primary"
              variant="contained"
              disabled={!editingProperty?.name?.trim()}
            >
              Guardar
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={propertyDialogOpen} onClose={closeNewPropertyDialog} fullWidth maxWidth="sm">
          <DialogTitle>Nueva propiedad</DialogTitle>
          <DialogContent dividers>
            <div className={classes.dialogGrid}>
              <TextField
                label="Nombre"
                value={newProperty.name}
                onChange={event =>
                  setNewProperty(prevState => ({ ...prevState, name: event.target.value }))
                }
                variant="outlined"
                fullWidth
                autoFocus
              />
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={newProperty.type}
                  renderValue={renderTypeValue}
                  onChange={event => {
                    const nextType = event.target.value;
                    setNewProperty(prevState => ({
                      ...prevState,
                      type: nextType,
                      options: ["select", "multiSelect", "status"].includes(nextType)
                        ? prevState.options?.length
                          ? prevState.options
                          : getDefaultNewProperty().options
                        : [],
                      relationBaseId:
                        nextType === "relation"
                          ? prevState.relationBaseId ||
                            selectableBases[0]?.id ||
                            state.bases[0]?.id ||
                            ""
                          : prevState.relationBaseId,
                      relationLimit:
                        nextType === "relation" ? prevState.relationLimit || "none" : prevState.relationLimit,
                      bidirectional:
                        nextType === "relation" ? prevState.bidirectional !== false : prevState.bidirectional,
                    }));
                  }}
                  label="Tipo"
                >
                  {availablePropertyTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      <ListItemIcon>{type.icon}</ListItemIcon>
                      <ListItemText primary={type.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {newProperty.type === "relation" && (
                <div className={classes.relationSettings}>
                  <FormControl variant="outlined" fullWidth>
                    <InputLabel>Relacionada con</InputLabel>
                    <Select
                      value={newProperty.relationBaseId || selectableBases[0]?.id || state.bases[0]?.id || ""}
                      onChange={event =>
                        setNewProperty(prevState => ({
                          ...prevState,
                          relationBaseId: event.target.value,
                        }))
                      }
                      label="Relacionada con"
                    >
                      {(selectableBases.length ? selectableBases : state.bases).map(base => (
                        <MenuItem key={base.id} value={base.id}>
                          {base.name} {base.id === activeBase.id ? "(actual)" : ""}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl variant="outlined" fullWidth>
                    <InputLabel>Límite</InputLabel>
                    <Select
                      value={newProperty.relationLimit || "none"}
                      onChange={event =>
                        setNewProperty(prevState => ({
                          ...prevState,
                          relationLimit: event.target.value,
                        }))
                      }
                      label="Límite"
                    >
                      <MenuItem value="none">Sin límite</MenuItem>
                      <MenuItem value="one">Un registro</MenuItem>
                    </Select>
                  </FormControl>
                  <MenuItem
                    onClick={() =>
                      setNewProperty(prevState => ({
                        ...prevState,
                        bidirectional: !prevState.bidirectional,
                      }))
                    }
                  >
                    <Checkbox checked={Boolean(newProperty.bidirectional)} />
                    <ListItemText
                      primary="Relación bidireccional"
                      secondary={newProperty.bidirectional ? "Activado" : "Desactivado"}
                    />
                  </MenuItem>
                </div>
              )}
              {newProperty.type === "contactData" &&
                renderContactDataSettings(newProperty, setNewProperty)}
              {newProperty.type === "funnelStage" &&
                renderFunnelStageSettings(newProperty, setNewProperty)}
              {newProperty.type === "contactAction" &&
                renderContactActionSettings(newProperty, setNewProperty)}
              {newProperty.type === "currency" &&
                renderCurrencySettings(newProperty, setNewProperty)}
              {showOptionsEditor && (
                <div className={classes.optionEditor}>
                  <Typography variant="subtitle2">Opciones y colores</Typography>
                  {newProperty.options.map(option => (
                    <div className={classes.optionRow} key={option.id}>
                      <Button
                        className={classes.colorButton}
                        style={{ backgroundColor: option.color }}
                        onClick={() => {
                          const index = optionPalette.indexOf(option.color);
                          updateNewOption(option.id, {
                            color: optionPalette[(index + 1) % optionPalette.length],
                          });
                        }}
                      />
                      <TextField
                        value={option.label}
                        onChange={event => updateNewOption(option.id, { label: event.target.value })}
                        variant="outlined"
                        size="small"
                      />
                      <IconButton size="small" onClick={() => removeNewOption(option.id)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </div>
                  ))}
                  <Button color="primary" startIcon={<AddIcon />} onClick={addNewOption}>
                    Agregar opción
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeNewPropertyDialog} color="secondary">
              Cancelar
            </Button>
            <Button onClick={addProperty} color="primary" variant="contained" disabled={!newProperty.name.trim()}>
              Crear propiedad
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </MainContainer>
  );
};

export default WorkspaceData;
